<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick, readonly } from 'vue'
import { AgGridVue } from 'ag-grid-vue3'
import { themeQuartz, ModuleRegistry, TooltipModule } from 'ag-grid-community'
import type { ColDef, GridApi, GridReadyEvent, CellValueChangedEvent, RowSelectedEvent, CellEditingStoppedEvent } from 'ag-grid-community'
import CustomTooltip from '@/components/ui/CustomTooltip.vue'
import stringToColor from 'string-to-color'
import { 
  BotanicalNameCell, 
  CommonNameCell, 
  CodeCell, 
  MeasurementCell, 
  DefaultCell 
} from '@/components/ui/cell-renderers'
// Remove old import - will use store instead

// Register the TooltipModule properly
ModuleRegistry.registerModules([TooltipModule])
import type { AutocompleteColumnConfig } from '@/types/autocomplete'
import { columnDisplayLabels } from '@/types/schedule'
import { Button } from '@/components/ui/button'
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem
} from '@/components/ui/dropdown-menu'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import DivergentSliderPicker from '@/components/ui/DivergentSliderPicker.vue'

// Add panel-related imports and types
import type { Panel as PanelType } from '@/types/projectdock'
import type { Tool } from '@/types/tools'

// Add cell response system
import { useCellResponseSystem, useBotanicalCellResponseSystem, type CellResponseRule } from '@/composables/useCellResponseSystem'

// Add panel communication imports
import { usePanelConnections } from '@/composables/usePanelConnections'
import { useLayoutStore } from '@edanweis/vue-code-layout'
import type { LayoutStore } from '@edanweis/vue-code-layout'
import { useMitter } from '#imports'
import { useDebounceFn, watchDebounced, useElementHover } from '@vueuse/core'
import { usePanelDataTransformers } from '@/composables/useTransformers'
import { usePanelData } from '@/composables/usePanelData'
import { useEmitter } from '@/composables/useEmitter'

// Add PlantGrid parser for file analysis processing
import { usePlantGrid } from '@/composables/tools/usePlantGrid'

// Add help video imports
import { useHelpVideos } from '@/composables/useHelpVideos'
import HelpVideoPopover from '@/components/HelpVideoPopover.vue'

// Add UI store for color scheme tracking
import { useUiStore } from '@/stores/uiStore'

// Import validation composables for names coloring
import { useValidation } from '@/composables/useValidation'
import { useGlobalValidationResults } from '@/composables/useGlobalValidationResults'
import { useValidationResultsStore } from '@/stores/validationResultsStore'
import { useBiosecurityResultsStore } from '@/stores/biosecurityResultsStore'
import { useDiversityResultsStore } from '@/stores/diversityResultsStore'

// Define components to make them available to AgGridVue
const components = {
  CustomTooltip,
  BotanicalNameCell,
  CommonNameCell,
  CodeCell,
  MeasurementCell,
  DefaultCell
}

interface TableRow {
  [key: string]: any
}

export interface ColumnDef extends ColDef {
  tooltip?: string | ((params: any) => string)
  editable?: boolean
  sortable?: boolean
  filterable?: boolean
  resizable?: boolean
  pinned?: 'left' | 'right' | null
  autocomplete?: {
    debounceWaitMs?: number
    fetch?: (cellEditor: any, text: any, update: any) => Promise<void> | void
    onSelect?: (cellEditor: any, selectedItem: any) => Promise<void> | void
    emptyMsg?: string
    showOnFocus?: boolean
    strict?: boolean
    onFreeTextSelect?: (cellEditor: any, userInput: string) => any
    minLength?: number
    className?: string
    render?: (cellEditor: any, item: any, currentValue: string) => HTMLElement
    renderGroup?: (cellEditor: any, name: string, currentValue: string) => HTMLElement
  }
}

// Add MitterEvents interface
interface MitterEvents {
  [key: string]: any;
  'panel:data:validate': { panelId: string; tool_uid: string; data: any; validation_tool_uid: string };
  'panel:toolData': { panelId: string; toolId: string; data: any };
  'plantgrid:row:set:botanicalname': any;
  'plantgrid:row:set:commonname': any;
  'plantgrid:row:set:code': any;
  'validation:diversity:updated': { readiness: number; timestamp: number };
}

// Add UpdateQueueItem interface for the update queue system
interface UpdateQueueItem {
  target: {
    columnName: string,
    cellValue: any
  },
  set: Record<string, any>,
  targetRowId?: string
}

interface Props {
  data?: TableRow[]
  columns?: ColumnDef[]
  schema?: any // Schema object like columnSchema from schedule.ts
  height?: string | number
  theme?: string
  loading?: boolean
  pagination?: boolean
  pageSize?: number
  rowSelection?: 'single' | 'multiple'
  enableSorting?: boolean
  enableFiltering?: boolean
  enableColumnResize?: boolean
  enableRowDrag?: boolean
  autoSizeColumns?: boolean
  suppressRowClickSelection?: boolean
  animateRows?: boolean
  defaultColDef?: ColDef
  enableAutocomplete?: boolean
  autocompleteConfigs?: Record<string, any> // Field name -> autocomplete config mapping
  enableZoom?: boolean
  showFloatingControls?: boolean
  showAddButton?: boolean
  checkboxSelection?: boolean
  autoFocusFirstCell?: boolean
  // New props for panel-based persistence
  panel?: PanelType
  tool?: Tool
  enablePanelPersistence?: boolean
  // Cell response system
  cellResponseRules?: CellResponseRule[]
  enableCellResponses?: boolean
  cellResponseLogging?: boolean
  // Transformers system
  enableTransformers?: boolean
  transformersConfig?: {
    asyncOperationsDisabled?: boolean
    debounceMs?: number
    enableLogging?: boolean
  }
  // Panel communication props
  enablePanelEvents?: boolean
  validationTools?: string[]
  emitPanelEvents?: boolean
  // Enhanced panel integration props
  enableAutoConnect?: boolean
  handleIncomingData?: boolean
  autoTransformData?: boolean
  onDataReceived?: (data: { sourcePanelId: string; sourceToolId: string; data: any }) => void
  // Advanced features from PlantGrid.vue
  enableUpdateQueue?: boolean
  enableMitterEvents?: boolean
  enableValidationDropdown?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  data: () => [],
  columns: () => [],
  schema: undefined,
  height: '400px',
  theme: 'ag-theme-alpine',
  loading: false,
  pagination: false,
  pageSize: 50,
  enableSorting: true,
  enableFiltering: true,
  enableColumnResize: true,
  enableRowDrag: false,
  autoSizeColumns: true,
  suppressRowClickSelection: false,
  animateRows: true,
  defaultColDef: () => ({}),
  enableAutocomplete: false,
  autocompleteConfigs: () => ({}),
  enableZoom: false,
  showFloatingControls: false,
  showAddButton: false,
  checkboxSelection: false,
  autoFocusFirstCell: true,
  // New props for panel-based persistence
  panel: undefined,
  tool: undefined,
  enablePanelPersistence: false,
  // Cell response system defaults
  cellResponseRules: () => [],
  enableCellResponses: false,
  cellResponseLogging: false,
  // Transformers system defaults
  enableTransformers: false,
  transformersConfig: () => ({
    asyncOperationsDisabled: false,
    debounceMs: 100,
    enableLogging: false
  }),
  // Panel communication props defaults - auto-enabled when panel+tool provided
  enablePanelEvents: false,
  validationTools: () => [],
  emitPanelEvents: false,
  // Enhanced panel integration props
  enableAutoConnect: false,
  handleIncomingData: false,
  autoTransformData: false,
  onDataReceived: () => {},
  // Advanced features defaults
  enableUpdateQueue: false,
  enableMitterEvents: false,
  enableValidationDropdown: false
})

// Auto-enable panel events when panel and tool are provided
const shouldEnablePanelEvents = computed(() => {
  return !!(props.panel && props.tool)
})

// Auto-configure panel features when panel system should be enabled
const effectivePanelEventsEnabled = computed(() => {
  return props.enablePanelEvents || shouldEnablePanelEvents.value
})

const effectiveAutoConnect = computed(() => {
  return props.enableAutoConnect || shouldEnablePanelEvents.value
})

const effectiveHandleIncomingData = computed(() => {
  return props.handleIncomingData || shouldEnablePanelEvents.value
})

const effectiveEmitPanelEvents = computed(() => {
  return props.emitPanelEvents || shouldEnablePanelEvents.value
})

const effectiveValidationTools = computed(() => {
  if (props.validationTools.length > 0) {
    return props.validationTools
  }
  // Provide default validation tools when panel system is auto-enabled
  if (shouldEnablePanelEvents.value) {
    return ['name-validation-tool', 'biosecurity-validation-tool', 'availability-validation-tool', 'diversity-validation-tool']
  }
  return []
})

// Auto-enable advanced features when panel system is enabled
const effectiveEnableUpdateQueue = computed(() => {
  return props.enableUpdateQueue || shouldEnablePanelEvents.value
})

const effectiveEnableMitterEvents = computed(() => {
  return props.enableMitterEvents || shouldEnablePanelEvents.value
})

const effectiveEnableValidationDropdown = computed(() => {
  return props.enableValidationDropdown || shouldEnablePanelEvents.value
})

const emit = defineEmits<{
  ready: [api: GridApi]
  cellValueChanged: [event: CellValueChangedEvent]
  cellEditingStopped: [event: CellEditingStoppedEvent]
  rowSelected: [event: RowSelectedEvent]
  selectionChanged: [selectedRows: any[]]
  addRow: []
  deleteSelectedRows: [selectedIndices: number[]]
  dataUpdate: [data: any[]]
}>()

const gridApi = ref<GridApi>()
const gridRef = ref()

// Update queue system from PlantGrid.vue
const updateQueue = ref<UpdateQueueItem[]>([])
const isProcessingUpdateQueue = ref(false)
const isActiveEditing = ref(false)

// Mitter events system
const mitter = useMitter()
const { emitMitter: emitValidationEvent, clearHashCache: clearValidationCache } = useEmitter()

// Help video system
const helpVideos = useHelpVideos()
const quantityColumnHeaderRef = ref<HTMLElement | null>(null)

// UI store for color scheme tracking
const uiStore = useUiStore()

// Validation results system from NameStatus.vue
// Import validation results store
const validationResultsStore = useValidationResultsStore()
const { getFamilyForName, getGenusForName, hasValidationResult } = validationResultsStore

// Import diversity results store
const diversityResultsStore = useDiversityResultsStore()
const { getRelativeAbundance, getScaledAbundance, violatesThresholds, hasDiversityResult } = diversityResultsStore

// Import biosecurity results store
const biosecurityResultsStore = useBiosecurityResultsStore()
const { getRiskLevel, getRiskScore, getScaledPestCount, hasBiosecurityResult } = biosecurityResultsStore

// Import availability results from validation store
const { getAvailabilityResults, hasAvailabilityResults } = validationResultsStore

// Get current project for availability lookups
const projectStore = useProjectStore()
const { selectedProject } = storeToRefs(projectStore)

// Global availability results cache that gets updated when new results come in
const globalAvailabilityResults = ref<Record<string, any>>({})

// Listen for availability results updates
const availabilityResultsListener = (results: Record<string, any>) => {
  console.log('[AgGrid] Received availability results for grid coloring:', Object.keys(results))
  globalAvailabilityResults.value = { ...globalAvailabilityResults.value, ...results }
  
  // Force color cache invalidation and grid refresh
  colorCacheVersion.value++
  if (gridApi.value) {
    gridApi.value.refreshCells({ force: true })
  }
}

// Set up availability results listener
mitter.listen('availability:validation:results' as any, availabilityResultsListener)

// Helper function to get availability status for a species
const getAvailabilityStatus = (botanicalName: string): 'available' | 'unavailable' | null => {
  if (!botanicalName?.trim()) return null
  
  // Look up in global availability results cache
  const result = globalAvailabilityResults.value[botanicalName]
  if (!result) return null
  
  return result.isAvailable ? 'available' : 'unavailable'
}

// Check if we have availability data for any species
const hasAvailabilityData = (): boolean => {
  if (!selectedProject.value?.id) return false
  
  // Check if any cached availability results exist
  // This is a simplified check - in practice we'd need to track which species have been validated
  return true // For now, assume availability validation is always possible
}

// Listen for diversity validation results to trigger grid re-render
mitter.listen('diversity:validation:results' as any, () => {
  console.log('[AgGrid] Received diversity validation results, refreshing colors')
  // Force color cache invalidation and grid refresh
  colorCacheVersion.value++
  if (gridApi.value) {
    gridApi.value.refreshCells({ force: true })
  }
})



// Access validation composables for names coloring  
const { nameStatus: legacyNameStatus } = useValidation()
const { } = useGlobalValidationResults()

// Create a computed property to get validation data from the store
const nameStatus = computed(() => {
  const storeResults = validationResultsStore.getValidationResults
  
  // Convert store data to the format expected by names validation
  const exact: any[] = []
  const inexact: any[] = []
  const invalid: any[] = []
  
  storeResults.forEach((result, originalName) => {
    if (result.matchedName) {
      // We have a match - determine if it's exact or inexact based on score/similarity
      if (result.score > 0.9) {
        exact.push({
          original: result.originalName,
          matched: result.matchedName,
          source: result.source || 'unknown'
        })
      } else {
        inexact.push({
          original: result.originalName,
          matched: result.matchedName,
          source: result.source || 'unknown'
        })
      }
    } else {
      // No match found
      invalid.push({
        original: result.originalName,
        matched: null,
        source: result.source || 'unknown'
      })
    }
  })
  
  return { exact, inexact, invalid }
})

// Setup help video for quantity column header
const quantityColumnHelp = helpVideos.handleElementHelp('quantity-column-help', quantityColumnHeaderRef, {
  dismissOnDistance: false
})

// Function to find and set the quantity column header element
const findQuantityColumnHeader = () => {
  if (!gridRef.value) return
  
  // Find the quantity column header by col-id="qty"
  const headerElement = gridRef.value.$el?.querySelector('[col-id="qty"]')
  if (headerElement) {
    quantityColumnHeaderRef.value = headerElement as HTMLElement
  }
}

// Biodiversity readiness checker - simulated for now
// In a real implementation, this would connect to the actual biodiversity calculation
const biodiversityReadiness = ref(10) // Default value above threshold

// Watch for biodiversity readiness changes and show help when below 5%
watch(biodiversityReadiness, (newValue) => {
  if (newValue < 5 && quantityColumnHeaderRef.value) {
    // Reset dismissed state so the help video can appear again
    quantityColumnHelp.reset()
    quantityColumnHelp.show()
  }
}, { immediate: true })

// Listen for biodiversity readiness updates from DiversityStatus component
const biodiversityReadinessListener = (event: any) => {
  if (event?.readiness !== undefined) {
    console.log(`[AgGrid] Received biodiversity readiness update: ${event.readiness}%`)
    biodiversityReadiness.value = event.readiness
  }
}

// Setup mitter listener for biodiversity updates
mitter.listen('validation:diversity:updated', biodiversityReadinessListener)

// Method to update biodiversity readiness from external components
const updateBiodiversityReadiness = (value: number) => {
  biodiversityReadiness.value = value
}

// Color picker state - slider selections
const selectedColorRange = ref<any[]>([])

// Color palettes for gradient circles (from DivergentSlider.vue)
const colorPalettes = {
  sequential_centered: ['#9E0142', '#D53E4F', '#F46D43', '#FDAE61', '#FEE08B', '#E6F598', '#ABDDA4', '#66C2A5', '#3288BD', '#5E4FA2'],
  sequential: ['#440154', '#414487', '#2A788E', '#22A884', '#7AD151', '#FDE725'],
  divergent: ['#002BFF', '#0066FF', '#0099FF', '#00CCFF', '#00FFFF', '#66FFCC', '#99FF99', '#CCFF66', '#FFFF00', '#FFCC00', '#FF9900', '#FF2B00'],
  qualitative: ['#A6CEE3', '#1F78B4', '#B2DF8A', '#33A02C', '#FB9A99', '#E31A1C', '#FDBF6F', '#FF7F00', '#CAB2D6', '#6A3D9A', '#FFFF99', '#B15928'],
  rag: ['#22C55E', '#F59E0B','#EF4444'] // Green for good, amber for caution, red for critical
}

// Interface for slider selections (matching DivergentSliderPicker)
interface SliderSelection {
  id: string
  method: string
  column?: string
  palette: string
  range: [number, number]
  label: string
  isActive?: boolean
}

// Color application state
const colorCacheVersion = ref(0)
const colorValueCache = ref(new Map<string, { value: number; color: string }>())

// Helper function to extract numeric value from cell data for coloring
const extractNumericValue = (cellValue: any, method: string, column?: string, rowData?: any): number | null => {
  if (method === 'column' && column) {
    // For column-based coloring, extract the actual value
    if (typeof cellValue === 'object' && cellValue !== null && 'value' in cellValue) {
      const val = parseFloat(cellValue.value)
      return !isNaN(val) ? val : null
    }
    const val = parseFloat(String(cellValue || ''))
    return !isNaN(val) ? val : null
  }
  
  // For row-level methods, analyze the entire row data
  if (!rowData) return null
  
  switch (method) {
    case 'diversity':
      // Get botanical name from row data for diversity lookup
      const diversityBotanicalNameData = rowData?.botanicalname
      let diversityBotanicalName = ''
      
      if (diversityBotanicalNameData && typeof diversityBotanicalNameData === 'object') {
        diversityBotanicalName = diversityBotanicalNameData.label || diversityBotanicalNameData.value || ''
      } else if (typeof diversityBotanicalNameData === 'string') {
        diversityBotanicalName = diversityBotanicalNameData
      }
      
      if (!diversityBotanicalName || !diversityBotanicalName.trim()) return null
      
      // Check if we have diversity data for this species
      if (!hasDiversityResult(diversityBotanicalName)) return null
      
      // Return relative abundance for colormaps
      const relativeAbundance = getRelativeAbundance(diversityBotanicalName)
      if (relativeAbundance !== null) {
        return relativeAbundance // 0-1 value representing species abundance
      }
      
      return null
      
    case 'biosecurity':
      // Get botanical name from row data for biosecurity lookup
      const biosecurityBotanicalNameData = rowData?.botanicalname
      let biosecurityBotanicalName = ''
      
      if (biosecurityBotanicalNameData && typeof biosecurityBotanicalNameData === 'object') {
        biosecurityBotanicalName = biosecurityBotanicalNameData.label || biosecurityBotanicalNameData.value || ''
      } else if (typeof biosecurityBotanicalNameData === 'string') {
        biosecurityBotanicalName = biosecurityBotanicalNameData
      }
      
      if (!biosecurityBotanicalName || !biosecurityBotanicalName.trim()) return null
      
      // Check if we have biosecurity data for this species
      if (!hasBiosecurityResult(biosecurityBotanicalName)) return null
      
      // Return risk score for colormaps (0-1 value representing pest count or risk level)
      const riskScore = getRiskScore(biosecurityBotanicalName)
      if (riskScore !== null) {
        return riskScore // 0-1 value representing normalized pest count or risk
      }
      
      return null
      
    case 'availability':
      // Get botanical name from row data for availability lookup
      const availabilityBotanicalNameData = rowData?.botanicalname
      let availabilityBotanicalName = ''
      
      if (availabilityBotanicalNameData && typeof availabilityBotanicalNameData === 'object') {
        availabilityBotanicalName = availabilityBotanicalNameData.label || availabilityBotanicalNameData.value || ''
      } else if (typeof availabilityBotanicalNameData === 'string') {
        availabilityBotanicalName = availabilityBotanicalNameData
      }
      
      if (!availabilityBotanicalName || !availabilityBotanicalName.trim()) return null
      
      // Get availability data from global cache
      const availabilityData = globalAvailabilityResults.value[availabilityBotanicalName]
      if (!availabilityData) return null
      
      // For RAG coloring, use binary status
      if (method === 'availability' && rowData) {
        // Check if this is being called for RAG palette specifically
        // If so, return binary values for available/unavailable
        return availabilityData.isAvailable ? 0.1 : 0.9
      }
      
      // For divergent/sequential coloring, calculate total quantity from all suppliers
      if (availabilityData.suppliers && Array.isArray(availabilityData.suppliers)) {
        const totalQuantity = availabilityData.suppliers.reduce((sum: number, supplier: any) => {
          return sum + (supplier.quantity || 0)
        }, 0)
        
        // Return the total quantity (will be normalized later for color mapping)
        return totalQuantity
      }
      
      // Fallback: return 0 for unavailable species
      return availabilityData.isAvailable ? 1 : 0
      
    case 'names':
      // For names validation coloring, check validation status
      const botanicalNameForValidation = rowData?.botanicalname
      let nameToValidate = ''
      
      if (botanicalNameForValidation && typeof botanicalNameForValidation === 'object') {
        nameToValidate = botanicalNameForValidation.label || botanicalNameForValidation.value || ''
      } else if (typeof botanicalNameForValidation === 'string') {
        nameToValidate = botanicalNameForValidation
      }
      
      if (!nameToValidate || !nameToValidate.trim()) return null
      
            // Check validation status in nameStatus results
      if (nameStatus.value) {
        // Use the pre-computed classification arrays directly
        const nameToCheck = nameToValidate.toLowerCase()
        
        // Check if this name is in the exact matches array
        const isExact = nameStatus.value.exact?.some(match => 
          match.original?.toLowerCase() === nameToCheck
        )
        if (isExact) return 0.1 // Low value for green (exact match - start of palette)
        
        // Check if this name is in the inexact matches array  
        const isInexact = nameStatus.value.inexact?.some(match => 
          match.original?.toLowerCase() === nameToCheck
        )
        if (isInexact) return 0.5 // Middle value for amber (partial match)
        
        // Check if this name is in the invalid matches array
        const isInvalid = nameStatus.value.invalid?.some(match => 
          match.original?.toLowerCase() === nameToCheck
        )
        if (isInvalid) return 0.9 // High value for red (failed match - end of palette)
      }
      // No validation data available - return null (no color)
      return null
      
    case 'family':
      // For plant families, use family data from NameStatus.vue validation results
      let familyStr = ''
      
      // Get botanical name from row data
      const botanicalNameData = rowData?.botanicalname
      let botanicalNameToLookup = ''
      
      if (botanicalNameData && typeof botanicalNameData === 'object') {
        botanicalNameToLookup = botanicalNameData.label || botanicalNameData.value || ''
      } else if (typeof botanicalNameData === 'string') {
        botanicalNameToLookup = botanicalNameData
      }
      
      // Try to get family from validation results first
      if (botanicalNameToLookup && hasValidationResult(botanicalNameToLookup)) {
        familyStr = getFamilyForName(botanicalNameToLookup) || ''
      }
      
      // Fallback to cell value if no validation family data
      if (!familyStr && cellValue) {
        familyStr = String(cellValue || '')
      }
      
      if (!familyStr || !familyStr.trim()) return null
      
      // Create a simple hash from the family name to get a 0-1 value for slider range
      const cleanFamily = familyStr.trim()
      let familyHash = 0
      for (let i = 0; i < cleanFamily.length; i++) {
        const char = cleanFamily.charCodeAt(i)
        familyHash = ((familyHash << 5) - familyHash) + char
        familyHash = familyHash & familyHash // Convert to 32-bit integer
      }
      
      // Return normalized value (0-1) for slider range comparison
      return Math.abs(familyHash) % 100 / 100
      
    default:
      return null
  }
}

// Helper function to convert hex color to RGB
const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 0, g: 0, b: 0 }
}

// Helper function to convert RGB to hex
const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (n: number) => {
    const hex = Math.round(n).toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

// Helper function to interpolate between colors in a palette with smooth gradients
const interpolateColor = (value: number, colors: string[]): string => {
  if (value <= 0) return colors[0]
  if (value >= 1) return colors[colors.length - 1]
  
  const position = value * (colors.length - 1)
  const index = Math.floor(position)
  const fraction = position - index
  
  if (index >= colors.length - 1) return colors[colors.length - 1]
  
  // Get the two colors to interpolate between
  const color1 = colors[index]
  const color2 = colors[index + 1]
  
  // Convert hex colors to RGB
  const rgb1 = hexToRgb(color1)
  const rgb2 = hexToRgb(color2)
  
  // Interpolate between RGB values
  const r = rgb1.r + (rgb2.r - rgb1.r) * fraction
  const g = rgb1.g + (rgb2.g - rgb1.g) * fraction
  const b = rgb1.b + (rgb2.b - rgb1.b) * fraction
  
  // Convert back to hex
  return rgbToHex(r, g, b)
}

// Helper function to get color for a value based on selection
const getColorForValue = (value: number, selection: SliderSelection, rawData?: any): string | null => {
  const [rangeStart, rangeEnd] = selection.range
  
  // Special handling for diversity coloring
  if (selection.method === 'diversity' && rawData) {
    // Get botanical name from row data
    const diversityBotanicalNameData = rawData.botanicalname
    let diversityBotanicalName = ''
    
    if (diversityBotanicalNameData && typeof diversityBotanicalNameData === 'object') {
      diversityBotanicalName = diversityBotanicalNameData.label || diversityBotanicalNameData.value || ''
    } else if (typeof diversityBotanicalNameData === 'string') {
      diversityBotanicalName = diversityBotanicalNameData
    }
    
    // Debug: Log every diversity color request
    console.log(`[AgGrid] Diversity color requested for: "${diversityBotanicalName}", palette: ${selection.palette}`)
    
    if (diversityBotanicalName && diversityBotanicalName.trim()) {
      // RAG coloring: discrete red/green based on threshold violations
      if (selection.palette === 'rag') {
        const violatesThreshold = violatesThresholds(diversityBotanicalName)
        const diversityData = diversityResultsStore.getDiversityDataForName(diversityBotanicalName)
        
        // Check range constraints using relative abundance for RAG palette
        const relativeAbundance = getRelativeAbundance(diversityBotanicalName)
        if (relativeAbundance !== null) {
          if (relativeAbundance < rangeStart || relativeAbundance > rangeEnd) {
            return null // Outside slider range, don't apply color
          }
        }
        
        // Debug logging for RAG coloring
        console.log(`[AgGrid] RAG color for ${diversityBotanicalName}:`, {
          violatesThreshold,
          relativeAbundance,
          rangeStart,
          rangeEnd,
          diversityData: diversityData ? {
            relativeAbundance: `${(diversityData.relativeAbundance * 100).toFixed(1)}%`,
            violatesSpecies: diversityData.violatesSpeciesThreshold,
            violatesGenus: diversityData.violatesGenusThreshold,
            violatesFamily: diversityData.violatesFamilyThreshold
          } : 'No data found',
          colorResult: violatesThreshold ? 'RED' : 'GREEN'
        })
        
        if (violatesThreshold) {
          return colorPalettes.rag[2] // Red for threshold violations
        } else {
          return colorPalettes.rag[0] // Green for compliant species  
        }
      }
      
      // Sequential coloring: use linearly scaled abundance for full color range
      if (selection.palette === 'sequential') {
        const scaledValue = getScaledAbundance(diversityBotanicalName)
        if (scaledValue !== null) {
          // Check range constraints
          if (scaledValue < rangeStart || scaledValue > rangeEnd) {
            return null
          }
          // Use linearly scaled value that maps actual data range to full colormap
          const colors = colorPalettes.sequential
          return interpolateColor(scaledValue, colors)
        }
      }
      
      // For other palette types, fall through to use the original relative abundance value
    }
    return null
  }

  // Special handling for availability coloring
  if (selection.method === 'availability' && rawData) {
    // Get botanical name from row data
    const availabilityBotanicalNameData = rawData.botanicalname
    let availabilityBotanicalName = ''
    
    if (availabilityBotanicalNameData && typeof availabilityBotanicalNameData === 'object') {
      availabilityBotanicalName = availabilityBotanicalNameData.label || availabilityBotanicalNameData.value || ''
    } else if (typeof availabilityBotanicalNameData === 'string') {
      availabilityBotanicalName = availabilityBotanicalNameData
    }
    
    // Debug: Log every availability color request
    console.log(`[AgGrid] Availability color requested for: "${availabilityBotanicalName}", palette: ${selection.palette}`)
    
    if (availabilityBotanicalName && availabilityBotanicalName.trim()) {
      // RAG coloring: discrete colors based on availability status
      if (selection.palette === 'rag') {
        const availabilityStatus = getAvailabilityStatus(availabilityBotanicalName)
        
        // For RAG availability, check if the specific status value falls within range
        let statusValue = null
        if (availabilityStatus === 'available') {
          statusValue = 0.1 // Green value
        } else if (availabilityStatus === 'unavailable') {
          statusValue = 0.9 // Red value
        }
        
        // Check range constraints using the actual status value
        if (statusValue === null || statusValue < rangeStart || statusValue > rangeEnd) {
          return null // Outside slider range or unknown status, don't apply color
        }
        
        // Debug logging for RAG coloring
        console.log(`[AgGrid] RAG color for ${availabilityBotanicalName}:`, {
          availabilityStatus,
          statusValue,
          rangeStart,
          rangeEnd,
          colorResult: availabilityStatus === 'available' ? 'GREEN' : availabilityStatus === 'unavailable' ? 'RED' : 'NONE'
        })
        
        if (availabilityStatus === 'available') {
          return colorPalettes.rag[0] // Green for available
        } else if (availabilityStatus === 'unavailable') {
          return colorPalettes.rag[2] // Red for unavailable
        }
        // Return null for unknown status - no color applied
        return null
      }
      
      // For divergent and sequential palettes, use total quantity from suppliers
      if (selection.palette === 'divergent' || selection.palette === 'sequential') {
        const availabilityData = globalAvailabilityResults.value[availabilityBotanicalName]
        if (availabilityData?.suppliers && Array.isArray(availabilityData.suppliers)) {
          const totalQuantity = availabilityData.suppliers.reduce((sum: number, supplier: any) => {
            return sum + (supplier.quantity || 0)
          }, 0)
          
          // Get all availability data to find min/max for normalization
          const allQuantities = Object.values(globalAvailabilityResults.value)
            .filter(data => data.suppliers && Array.isArray(data.suppliers))
            .map(data => data.suppliers.reduce((sum: number, supplier: any) => sum + (supplier.quantity || 0), 0))
            .filter(qty => qty > 0)
          
          if (allQuantities.length === 0) return null
          
          const minQuantity = Math.min(...allQuantities)
          const maxQuantity = Math.max(...allQuantities)
          
          // Normalize total quantity to 0-1 range (inverted: larger quantities = lighter shades)
          let normalizedValue = 0
          if (maxQuantity > minQuantity) {
            normalizedValue = 1 - ((totalQuantity - minQuantity) / (maxQuantity - minQuantity))
          } else if (totalQuantity > 0) {
            normalizedValue = 0.5 // If all quantities are equal, use middle value
          }
          
          // Check range constraints
          if (normalizedValue < rangeStart || normalizedValue > rangeEnd) {
            return null
          }
          
          // Debug logging
          console.log(`[AgGrid] ${selection.palette} color for ${availabilityBotanicalName}:`, {
            totalQuantity,
            minQuantity,
            maxQuantity,
            normalizedValue,
            rangeStart,
            rangeEnd
          })
          
          // Use the palette for quantity-based coloring
          const colors = colorPalettes[selection.palette as keyof typeof colorPalettes] || colorPalettes.divergent
          return interpolateColor(normalizedValue, colors)
        }
        return null
      }
      
      // For other palette types, use the numeric value
      const numericValue = value
      if (numericValue !== null) {
        // Check range constraints
        if (numericValue < rangeStart || numericValue > rangeEnd) {
          return null
        }
        
        // Use the palette for other colormap types
        const colors = colorPalettes[selection.palette as keyof typeof colorPalettes] || colorPalettes.divergent
        return interpolateColor(numericValue, colors)
      }
    }
    return null
  }

  // Special handling for biosecurity coloring
  if (selection.method === 'biosecurity' && rawData) {
    // Get botanical name from row data
    const biosecurityBotanicalNameData = rawData.botanicalname
    let biosecurityBotanicalName = ''
    
    if (biosecurityBotanicalNameData && typeof biosecurityBotanicalNameData === 'object') {
      biosecurityBotanicalName = biosecurityBotanicalNameData.label || biosecurityBotanicalNameData.value || ''
    } else if (typeof biosecurityBotanicalNameData === 'string') {
      biosecurityBotanicalName = biosecurityBotanicalNameData
    }
    
    // Debug: Log every biosecurity color request
    console.log(`[AgGrid] Biosecurity color requested for: "${biosecurityBotanicalName}", palette: ${selection.palette}`)
    
    if (biosecurityBotanicalName && biosecurityBotanicalName.trim()) {
      // RAG coloring: discrete colors based on risk level
      if (selection.palette === 'rag') {
        const riskLevel = getRiskLevel(biosecurityBotanicalName)
        const biosecurityData = biosecurityResultsStore.getBiosecurityDataForName(biosecurityBotanicalName)
        
        // Check range constraints using risk score for RAG palette
        const riskScore = getRiskScore(biosecurityBotanicalName)
        if (riskScore !== null) {
          if (riskScore < rangeStart || riskScore > rangeEnd) {
            return null // Outside slider range, don't apply color
          }
        }
        
        // Debug logging for RAG coloring
        console.log(`[AgGrid] RAG color for ${biosecurityBotanicalName}:`, {
          riskLevel,
          riskScore,
          rangeStart,
          rangeEnd,
          biosecurityData: biosecurityData ? {
            totalPests: biosecurityData.totalPests,
            majorHostCount: biosecurityData.majorHostCount,
            hostCount: biosecurityData.hostCount,
            weedWildCount: biosecurityData.weedWildCount
          } : 'No data found',
          colorResult: riskLevel === 'major_host' ? 'RED' : riskLevel === 'host' ? 'ORANGE' : riskLevel === 'weed_wild' ? 'GREEN' : 'NONE'
        })
        
        if (riskLevel === 'major_host') {
          return colorPalettes.rag[2] // Red for major host (highest risk)
        } else if (riskLevel === 'host') {
          return colorPalettes.rag[1] // Orange for host (medium risk)
        } else if (riskLevel === 'weed_wild') {
          return colorPalettes.rag[0] // Green for weed/wild (low risk)
        }
        // Return null for 'none' (no pests) - no color applied
        return null
      }
      
      // Sequential coloring: use linearly scaled pest count for full color range
      if (selection.palette === 'sequential') {
        const scaledValue = getScaledPestCount(biosecurityBotanicalName)
        if (scaledValue !== null) {
          // Check range constraints
          if (scaledValue < rangeStart || scaledValue > rangeEnd) {
            return null
          }
          // Use linearly scaled value that maps actual data range to full colormap
          const colors = colorPalettes.sequential
          return interpolateColor(scaledValue, colors)
        }
      }
      
      // Divergent coloring: use normalized risk score
      if (selection.palette === 'divergent') {
        const riskScore = getRiskScore(biosecurityBotanicalName)
        if (riskScore !== null) {
          // Check range constraints
          if (riskScore < rangeStart || riskScore > rangeEnd) {
            return null
          }
          // Use risk score for divergent colormap
          const colors = colorPalettes.divergent
          return interpolateColor(riskScore, colors)
        }
      }
      
      // For other palette types, fall through to use the original risk score value
    }
    return null
  }
  
  // For family coloring, handle differently - use string-to-color and check range afterwards
  if (selection.method === 'family' && rawData) {
    let familyStr = ''
    
    // Get botanical name from row data (same logic as extractNumericValue)
    const botanicalNameData = rawData.botanicalname
    let botanicalNameToLookup = ''
    
    if (botanicalNameData && typeof botanicalNameData === 'object') {
      botanicalNameToLookup = botanicalNameData.label || botanicalNameData.value || ''
    } else if (typeof botanicalNameData === 'string') {
      botanicalNameToLookup = botanicalNameData
    }
    
    // Try to get family from validation results first
    if (botanicalNameToLookup && hasValidationResult(botanicalNameToLookup)) {
      familyStr = getFamilyForName(botanicalNameToLookup) || ''
    }
    
    if (familyStr && familyStr.trim()) {
      // Check if the hash value falls within the slider range - CROP, don't remap
      if (value < rangeStart || value > rangeEnd) {
        return null // No color applied outside range
      }
      return stringToColor(familyStr.trim())
    }
    return null
  }
  
  // For other methods, check range first - CROP, don't remap
  if (value < rangeStart || value > rangeEnd) {
    return null // No color applied outside range
  }
  
  // Map the value to the full palette range (0-1) instead of normalizing within slider range
  // This ensures the color represents the actual data value, not the slider-constrained range
  const colors = colorPalettes[selection.palette as keyof typeof colorPalettes] || colorPalettes.divergent
  
  return interpolateColor(value, colors)
}

// Data range cache for column normalization
const dataRanges = ref(new Map<string, { min: number; max: number }>())

// Function to calculate data ranges for all numeric columns
const calculateDataRanges = () => {
  if (!gridApi.value) return
  
  const ranges = new Map<string, { min: number; max: number }>()
  const columnData = new Map<string, number[]>()
  
  // Collect all numeric values for each column
  gridApi.value.forEachNode((node) => {
    if (!node.data) return
    
    Object.entries(node.data).forEach(([key, value]) => {
      const numValue = extractNumericValue(value, 'column', key)
      if (numValue !== null) {
        if (!columnData.has(key)) {
          columnData.set(key, [])
        }
        columnData.get(key)!.push(numValue)
      }
    })
  })
  
  // Calculate min/max for each column
  columnData.forEach((values, column) => {
    if (values.length > 0) {
      ranges.set(column, {
        min: Math.min(...values),
        max: Math.max(...values)
      })
    }
  })
  
  dataRanges.value = ranges
}

// Normalize column value to 0-1 range
const normalizeColumnValue = (value: number, column: string): number => {
  const range = dataRanges.value.get(column)
  if (!range || range.min === range.max) return 0.5 // Default to middle if no range
  
  return (value - range.min) / (range.max - range.min)
}

// Main function to calculate cell style based on color selections
const calculateCellColor = (params: any): string | null => {
  if (!selectedColorRange.value.length) return null
  
  // Process each color selection
  for (const selection of selectedColorRange.value) {
    if (!selection.isActive) continue

    let value: number | null = null
    let familyName = ''
    
    // Create cache key that includes selection range for family colors
    const selectionKey = selection.method === 'family' 
      ? `${selection.method}-${selection.range[0]}-${selection.range[1]}`
      : `${selection.method}-${selection.column || ''}-${selection.range[0]}-${selection.range[1]}`
    const cacheKey = `${params.node?.id || params.rowIndex}-${params.colDef?.field}-${colorCacheVersion.value}-${selectionKey}`
    
    // Check cache first
    if (colorValueCache.value.has(cacheKey)) {
      const cachedResult = colorValueCache.value.get(cacheKey)
      if (cachedResult?.color) {
        return cachedResult.color
      }
      continue // Try next selection if this one was cached as null
    }
    
    if (selection.method === 'column' && selection.column) {
      // Column-specific coloring
      if (params.colDef?.field === selection.column) {
        const rawValue = extractNumericValue(params.value, selection.method, selection.column)
        if (rawValue !== null) {
          // Normalize the value to 0-1 range for better color mapping
          value = normalizeColumnValue(rawValue, selection.column)
        }
      }
    } else if (selection.method === 'family') {
      // Family coloring - only apply to botanicalname column
      if (params.colDef?.field === 'botanicalname') {
        value = extractNumericValue(params.value, selection.method, undefined, params.data)
      }
    } else if (selection.method === 'names') {
      // Names validation coloring - only apply to botanicalname column
      if (params.colDef?.field === 'botanicalname') {
        value = extractNumericValue(params.value, selection.method, undefined, params.data)
      }
    } else {
      // Row-level coloring (diversity, biosecurity, availability, names, etc.)
      value = extractNumericValue(params.value, selection.method, undefined, params.data)
      
      // For family coloring, also extract the family name for the cache
      if (selection.method === 'family' && params.data) {
        const botanicalNameData = params.data.botanicalname
        let botanicalNameToLookup = ''
        
        if (botanicalNameData && typeof botanicalNameData === 'object') {
          botanicalNameToLookup = botanicalNameData.label || botanicalNameData.value || ''
        } else if (typeof botanicalNameData === 'string') {
          botanicalNameToLookup = botanicalNameData
        }
        
        if (botanicalNameToLookup && hasValidationResult(botanicalNameToLookup)) {
          familyName = getFamilyForName(botanicalNameToLookup) || ''
        }
      }
    }
    
    if (value !== null) {
      const color = getColorForValue(value, selection, params.data)
      
      // Cache the result
      colorValueCache.value.set(cacheKey, { 
        value, 
        color: color || ''
      })
      
      if (color) {
        return color
      }
    }
  }
  
  return null
}

// Cell style function for AG Grid
const cellStyle = (params: any) => {
  // Never apply colors to checkbox column
  if (params.colDef?.field === 'selected') {
    return null
  }
  
  const backgroundColor = calculateCellColor(params)
  
  if (backgroundColor) {
    return {
      '--cell-bg-color': backgroundColor + '30', // Store color as CSS variable
      color: '#000' // Ensure text is readable
    }
  }
  
  return null
}

// Row style function for AG Grid (for row-level coloring)
const rowStyle = (params: any) => {
  // For row-level methods like diversity, biosecurity, availability (excluding family which is cell-specific)
  const rowLevelMethods = selectedColorRange.value.filter(s => 
    s.isActive && ['diversity', 'biosecurity', 'availability'].includes(s.method)
  )
  
  if (!rowLevelMethods.length) return undefined
  
  // Use the first row-level method for row styling
  const selection = rowLevelMethods[0]
  const value = extractNumericValue(params.data, selection.method, undefined, params.data)
  
  if (value !== null) {
    const color = getColorForValue(value, selection, params.data)
    if (color) {
      return {
        backgroundColor: color + '20' // Very subtle row background
      }
    }
  }
  
  return undefined
}

// Generate gradients for active selections
const activeSelectionGradients = computed(() => {
  return selectedColorRange.value.map((selection: any) => {
    const colors = colorPalettes[selection.palette as keyof typeof colorPalettes] || colorPalettes.divergent
    
    // Create stepped gradient with hard color stops (matching DivergentSlider.vue)
    const steps = colors.length
    const stepSize = 100 / steps
    
    const colorStops = colors.map((color, index) => {
      const start = index * stepSize
      const end = (index + 1) * stepSize
      return `${color} ${start}%, ${color} ${end}%`
    }).join(', ')
    
    return `linear-gradient(to right, ${colorStops})`
  })
})

// Watch for color selection changes and refresh grid
watch(selectedColorRange, () => {
  // Increment cache version to invalidate cache
  colorCacheVersion.value++
  colorValueCache.value.clear()
  
  // Recalculate data ranges for new column selections
  if (gridApi.value) {
    calculateDataRanges()
  }
  
  // Update UI store with current active scheme status
  uiStore.setHasActiveColorSchemes(hasActiveSchemes.value)
  
  // Refresh grid cells to apply new colors
  if (gridApi.value) {
    nextTick(() => {
      setTimeout(() => {
        if (gridApi.value) {
          gridApi.value.redrawRows()
        }
      }, 0)
    })
  }
}, { deep: true })

// Watch validation results store for changes and trigger grid refresh
watch(
  () => validationResultsStore.getValidationResults.size,
  (newSize, oldSize) => {
    // Trigger redraw if we have family or names coloring active and results actually changed
    const hasFamilyColoring = selectedColorRange.value.some(s => s.isActive && s.method === 'family')
    const hasNamesColoring = selectedColorRange.value.some(s => s.isActive && s.method === 'names')
    
    if ((hasFamilyColoring || hasNamesColoring) && gridApi.value && newSize !== oldSize) {
      console.log('[AgGrid] Validation results size changed, refreshing validation colors')
      // Clear cache to force re-evaluation of validation colors
      colorCacheVersion.value++
      colorValueCache.value.clear()
      
      // Use a more gentle refresh approach - only refresh cells instead of full redraw
      nextTick(() => {
        setTimeout(() => {
          if (gridApi.value) {
            gridApi.value.refreshCells({ force: true })
          }
        }, 50) // Shorter delay for better responsiveness
      })
    }
  }
)

// Create data source computed property that handles both panel and prop-based data
const dataSource = computed({
  get: () => {
    if (props.enablePanelPersistence && props.panel?.data?.rowData) {
      return props.panel.data.rowData
    }
    return props.data
  },
  set: (newValue) => {
    if (props.enablePanelPersistence && props.panel) {
      if (!props.panel.data) {
        props.panel.data = { tool_uid: toolId.value }
      }
      props.panel.data.rowData = newValue
    }
    // Always emit data update regardless of persistence mode
    emit('dataUpdate', newValue)
  }
})


// Watch for data changes to recalculate ranges
watch(() => dataSource.value, () => {
  // Recalculate data ranges when data changes
  if (gridApi.value && selectedColorRange.value.length > 0) {
    setTimeout(() => {
      calculateDataRanges()
      // Refresh grid if colors are applied
      if (gridApi.value) {
        colorCacheVersion.value++
        colorValueCache.value.clear()
        gridApi.value.refreshCells({ force: true })
      }
    }, 100)
  }
}, { deep: true })

// Autocomplete loading state management - managed at grid level
const autocompleteLoadingCells = ref(new Map<string, boolean>())

// Provide loading state to cell renderers via context
const gridContext = computed(() => ({
  autocompleteLoadingCells: autocompleteLoadingCells.value
}))

// Autocomplete functionality
let AutocompleteSelectCellEditor: any = null
let CustomEscapeAwareEditor: any = null
const autocompleteLoaded = ref(false)

onMounted(async () => {
  if (props.enableAutocomplete && import.meta.client) {
    try {
      const { AutocompleteSelectCellEditor: ImportedEditor } = await import('ag-grid-autocomplete-editor')
      AutocompleteSelectCellEditor = ImportedEditor
      
      // Create custom editor with improved Escape handling
      CustomEscapeAwareEditor = class extends ImportedEditor {
        private initialValue: any
        private isDestroying: boolean = false
        
        constructor() {
          super()
        }
        
        override init(params: any) {
          super.init(params)
          this.initialValue = params.value
          
          const input = (this as any).eInput
          if (input) {
            input.addEventListener('keydown', (event: KeyboardEvent) => {
              if (event.key === 'Escape' && !this.isDestroying) {
                event.stopPropagation()
                event.preventDefault()
                // Use nextTick to avoid recursive stopEditing calls
                setTimeout(() => {
                  if (!this.isDestroying && params.api) {
                    params.api.stopEditing()
                    params.api.setFocusedCell(params.rowIndex, params.column)
                  }
                }, 0)
              }
            })
          }
        }
        
        override destroy() {
          this.isDestroying = true
          try {
            super.destroy()
          } catch (error) {
            // Silently handle destruction errors to prevent recursion
            console.warn('Error during editor destruction:', error)
          }
        }
        
        override getValue() {
          if ((this as any).currentItem) {
            return (this as any).currentItem
          }
          const input = (this as any).eInput
          return (input?.value) || this.initialValue
        }
      }
      
      autocompleteLoaded.value = true
    } catch (error) {
      console.error('Failed to load ag-grid-autocomplete-editor:', error)
    }
  }

  // Initialize panel connections if panel events are enabled
  if (effectivePanelEventsEnabled.value) {
    // console.log('[AgGrid] Initializing panel connections on mount')
    initializePanelConnections()
    
    // Auto-connect to default panels if enabled
    if (effectiveAutoConnect.value) {
      // Small delay to ensure panel connections are fully initialized
      setTimeout(() => {
        autoConnectToDefaults()
      }, 500)
    }
  }

  // Setup MitterEvents if enabled
  if (effectiveEnableMitterEvents.value) {
    setupMitterEventListeners()
  }
})

// Cleanup on unmount
onUnmounted(() => {
  if (props.enablePanelEvents && panelConnectionsRef.value) {
    try {
      // Cleanup panel connections
      // console.log('[AgGrid] Cleaning up panel connections')
      panelConnectionsRef.value = null
      layoutStoreRef.value = null
    } catch (error) {
      console.warn('[AgGrid] Error during cleanup:', error)
    }
  }

  // Cleanup MitterEvents
  if (effectiveEnableMitterEvents.value) {
    cleanupMitterEventListeners()
  }
})

// Theme and zoom functionality
const zoomLevel = ref(1)
const MIN_ZOOM = 0.5
const MAX_ZOOM = 2
const useFitContents = ref(true)

// Row management state
const selectedRowCount = ref(0)
const internalRowData = ref<TableRow[]>([])

// Undo/redo state
const undoAvailable = ref(false)
const redoAvailable = ref(false)

// Floating button position
const floatingButtonPosition = ref({ top: 0, visible: false })

// Cell response system setup - using botanical system for plant grids
const cellResponseSystem = props.enableTransformers 
  ? useBotanicalCellResponseSystem({
      enableLogging: props.cellResponseLogging,
      processingDebounceMs: 50, // Faster processing for plant transformations
      maxQueueSize: 1000
    })
  : useCellResponseSystem({
  enableLogging: props.cellResponseLogging,
  processingDebounceMs: 100,
  maxQueueSize: 1000
})

const asyncOperationsDisabled = ref(props.transformersConfig?.asyncOperationsDisabled || false)


// Panel-based persistence logic
const toolId = computed(() => props.tool?.id || 'agrid')

// Initialize panel data if panel persistence is enabled
if (props.enablePanelPersistence && props.panel) {
  if (!props.panel.data) {
    props.panel.data = { tool_uid: toolId.value }
  }
  if (!props.panel.data.rowData) {
    props.panel.data.rowData = []
  }
  if (!props.panel.data.columnDefs) {
    props.panel.data.columnDefs = []
  }
}

// Panel communication system setup
const layoutStoreRef = ref<LayoutStore | null>(null)
const panelConnectionsRef = ref<any>(null)
const isEmittingData = ref(false)

// Panel data transformers for inter-panel communication
const { transformData } = usePanelDataTransformers()

// Panel data management for registration and state sync
const { registerPanel, updatePanelData, getPanelData } = usePanelData()

// Validation tools system from PlantGrid.vue
const enabledValidationTools = ref<string[]>(['name-validation-tool', 'diversity-validation-tool', 'availability-validation-tool', 'biosecurity-validation-tool'])

// Review on change toggle
const reviewOnChange = ref(true)

// Initialize from panel data if available
if (props.panel?.data?.options?.reviewOnChange !== undefined) {
  reviewOnChange.value = props.panel.data.options.reviewOnChange
}

// Get parse function from usePlantGrid
const { parse } = usePlantGrid()

// Processing lock to prevent recursive updates
const isProcessingOutput = ref(false)

// MitterEvents setup from PlantGrid.vue
const setupMitterEventListeners = () => {
  // console.log('[AgGrid] Setting up MitterEvents listeners')
  
  // Listen for botanicalname updates
  mitter.listen('plantgrid:row:set:botanicalname' as any, async (data: any) => {
    // console.log('[AgGrid MitterEvents] plantgrid:row:set:botanicalname event received:', data)
    await handleMitterEvent('botanicalname', data)
  })

  // Listen for commonname updates
  mitter.listen('plantgrid:row:set:commonname' as any, async (data: any) => {
    // console.log('[AgGrid MitterEvents] plantgrid:row:set:commonname event received:', data)
    await handleMitterEvent('commonname', data)
  })

  // Listen for code updates
  mitter.listen('plantgrid:row:set:code' as any, async (data: any) => {
    // console.log('[AgGrid MitterEvents] plantgrid:row:set:code event received:', data)
    await handleMitterEvent('code', data)
  })
}

const cleanupMitterEventListeners = () => {
  // console.log('[AgGrid] Cleaning up MitterEvents listeners')
  // Cleanup logic would go here if needed
}

// Handle MitterEvents updates
const handleMitterEvent = async (fieldName: string, data: any) => {
  if (!gridApi.value || isProcessingUpdateQueue.value) {
    // console.log('[AgGrid MitterEvents] Skipping event - grid not ready or processing queue')
    return
  }

  try {
    const newValue = data[fieldName]
    if (newValue) {
      // console.log(`[AgGrid MitterEvents] Updating ${fieldName} with value:`, newValue)
      
      // Find and update the matching row
      await findAndUpdateGridRow(data.sourceColumn, data.sourceValue, {
        [fieldName]: newValue
      }, data.rowId)
    }
  } catch (error) {
    console.error(`[AgGrid MitterEvents] Error processing ${fieldName} event:`, error)
  }
}

// Find and update grid row (from PlantGrid.vue)
const findAndUpdateGridRow = async (sourceColumn: string, sourceValue: any, changes: Record<string, any>, targetRowId?: string) => {
  if (!gridApi.value) {
    // console.log('[AgGrid] Grid API not available, skipping update')
    return
  }
  
  // Finding grid row to update
  // console.log('[AgGrid] Finding grid row:', { sourceColumn, sourceValue, changes, targetRowId })
  
  let foundMatch = false
  
  // Find and update the row based on various criteria
  gridApi.value.forEachNode((node, index) => {
    if (foundMatch || !node.data) return
    
    // Try different strategies to match the row
    if (targetRowId && node.data._id === targetRowId) {
      // console.log(`[AgGrid] Match found at node ${index} by row ID match!`)
      foundMatch = true
      updateGridNode(node, changes)
    } else if (sourceColumn && sourceValue !== undefined) {
      const nodeValue = node.data[sourceColumn]
      let sourceMatch = false
      
      // Compare based on the type of values
      if (typeof nodeValue === 'object' && nodeValue !== null) {
        if (typeof sourceValue === 'object' && sourceValue !== null) {
          sourceMatch = (nodeValue.value === sourceValue.value) || (nodeValue.label === sourceValue.label)
        } else {
          sourceMatch = (nodeValue.value === sourceValue) || (nodeValue.label === sourceValue)
        }
      } else if (typeof sourceValue === 'object' && sourceValue !== null) {
        sourceMatch = (nodeValue === sourceValue.value) || (nodeValue === sourceValue.label)
      } else {
        sourceMatch = nodeValue === sourceValue
      }
      
      if (sourceMatch) {
        // console.log(`[AgGrid] Match found at node ${index} by sourceColumn match`)
        foundMatch = true
        updateGridNode(node, changes)
      }
    }
  })
  
  if (!foundMatch) {
    // console.log('[AgGrid] No matching row found, applying to first row as fallback')
    gridApi.value.forEachNode((node, index) => {
      if (!foundMatch && node.data && Object.keys(node.data).some(key => key !== '_id' && node.data[key])) {
        // console.log(`[AgGrid] Applying to first non-empty row at index ${index}`)
        foundMatch = true
        updateGridNode(node, changes)
      }
    })
  }
}

// Update grid node (from PlantGrid.vue)
const updateGridNode = (node: any, changes: Record<string, any>) => {
  // console.log('[AgGrid] Updating node with changes:', changes)
  
  const focusedCell = gridApi.value?.getFocusedCell()
  const columnsToRefresh: string[] = []
  
  Object.entries(changes).forEach(([key, value]) => {
    const gridKey = key.toLowerCase()
    columnsToRefresh.push(gridKey)
    
    try {
      if (value === null || value === undefined || value === '') {
        // console.log(`[AgGrid] Skipping ${gridKey} update - empty value`)
        return
      }
      
      // Handle different field types
      if (gridKey === 'botanicalname') {
        if (typeof value === 'object' && value !== null) {
          const existingData = node.data[gridKey]
          const metadata = (typeof existingData === 'object' && existingData !== null) 
            ? { ...existingData }
            : {}
          
          node.data[gridKey] = {
            ...metadata,
            ...value,
            source: value.source || metadata.source || 'transformer',
            updatedAt: new Date().toISOString(),
            genus: value.genus || metadata.genus || '',
            family: value.family || metadata.family || ''
          }
        } else {
          const existingData = node.data[gridKey]
          const metadata = (typeof existingData === 'object' && existingData !== null) 
            ? { ...existingData }
            : {}
          
          node.data[gridKey] = {
            ...metadata,
            label: String(value),
            value: String(value),
            source: 'transformer',
            updatedAt: new Date().toISOString(),
            genus: metadata.genus || '',
            family: metadata.family || ''
          }
        }
      } else if (gridKey === 'code') {
        const existingData = node.data[gridKey]
        if (typeof value !== 'object' || value === null) {
          const metadata = (typeof existingData === 'object' && existingData !== null) 
            ? { ...existingData }
            : {}
          
          node.data[gridKey] = {
            ...metadata,
            value: String(value),
            source: 'transformer',
            updatedAt: new Date().toISOString()
          }
        } else {
          node.data[gridKey] = value
        }
      } else {
        // Handle other fields
        const existingData = node.data[gridKey]
        
        if (typeof existingData === 'object' && existingData !== null && 'value' in existingData) {
          node.data[gridKey] = {
            ...existingData,
            value: typeof value === 'object' && value !== null && 'value' in value ? value.value : value,
            source: (typeof value === 'object' && value !== null && value.source) ? value.source : 'transformer',
            updatedAt: new Date().toISOString()
          }
        } else if (typeof value === 'object' && value !== null && 'value' in value) {
          node.data[gridKey] = {
            ...value,
            updatedAt: new Date().toISOString()
          }
        } else {
          node.data[gridKey] = {
            value: value,
            source: 'transformer',
            updatedAt: new Date().toISOString()
          }
        }
      }
    } catch (error) {
      console.error(`[AgGrid] Failed to update field ${gridKey}:`, error)
    }
  })
  
  // Refresh cells if we have changes
  if (columnsToRefresh.length > 0 && gridApi.value) {
    nextTick(() => {
      try {
        if (!gridApi.value) return
        
        // console.log(`[AgGrid] Refreshing columns:`, columnsToRefresh)
        
        gridApi.value.refreshCells({
          rowNodes: [node],
          columns: columnsToRefresh,
          force: true
        })
        
        // Restore focus
        if (focusedCell) {
          setTimeout(() => {
            if (gridApi.value) {
              gridApi.value.setFocusedCell(
                focusedCell.rowIndex,
                focusedCell.column.getId(),
                null
              )
            }
          }, 10)
        }
      } catch (error) {
        console.error(`[AgGrid] Failed to refresh grid cells:`, error)
      }
    })
  }
}

// Update queue processing (from PlantGrid.vue)
watch(updateQueue, async (newQueue) => {
  // console.log('[AgGrid] updateQueue changed, items:', newQueue.length, 'asyncDisabled:', asyncOperationsDisabled.value)
  
  if (asyncOperationsDisabled.value || isActiveEditing.value || isProcessingUpdateQueue.value || newQueue.length === 0) {
    return
  }
  
  isProcessingUpdateQueue.value = true
  
  try {
    while (updateQueue.value.length > 0) {
      if (isActiveEditing.value) {
        // console.log('[AgGrid] Editing started during queue processing, pausing')
        break
      }
      
      const item = updateQueue.value[0]
      // console.log('[AgGrid] Processing queue item:', JSON.stringify(item))
      
      const focusedCell = gridApi.value?.getFocusedCell()
      let foundMatch = false
      
      await nextTick()
      
      // Process the update
      if (item.targetRowId) {
        gridApi.value?.forEachNode((node, index) => {
          if (foundMatch) return
          if (!node.data) return
          
          if (node.data._id === item.targetRowId) {
            // console.log(`[AgGrid] Match found at node ${index} by row ID match!`)
            foundMatch = true
            updateGridNode(node, item.set)
          }
        })
      }
      
      if (!foundMatch) {
        gridApi.value?.forEachNode((node, index) => {
          if (foundMatch) return
          
          const nodeValue = node.data?.[item.target.columnName]
          
          if (node.data) {
            if (node.data[item.target.columnName] === item.target.cellValue) {
              // console.log(`[AgGrid] Match found at node ${index} by exact value match!`)
              foundMatch = true
              updateGridNode(node, item.set)
            } else if (focusedCell && node.rowIndex === focusedCell.rowIndex) {
              // console.log(`[AgGrid] Match found at node ${index} because it's the focused row!`)
              foundMatch = true
              updateGridNode(node, item.set)
            }
          }
        })
      }
      
      if (!foundMatch) {
        // console.log('[AgGrid] No matching row found for update. Applying to first row as fallback.')
        gridApi.value?.forEachNode((node, index) => {
          if (!foundMatch && node.data && Object.keys(node.data).some(key => key !== '_id' && node.data[key])) {
            // console.log(`[AgGrid] Applying to first non-empty row at index ${index}`)
            foundMatch = true
            updateGridNode(node, item.set)
          }
        })
      }
      
      if (isActiveEditing.value) {
        // console.log('[AgGrid] Editing started, pausing update queue processing')
        break
      }
      
      await new Promise(resolve => {
        const checkAndResolve = () => {
          if (isActiveEditing.value) {
            setTimeout(checkAndResolve, 100)
          } else {
            resolve(undefined)
          }
        }
        setTimeout(checkAndResolve, 20)
      })
      
      if (isActiveEditing.value) {
        // console.log('[AgGrid] Editing started, pausing update queue processing')
        break
      }
      
      if (updateQueue.value.length > 0) {
        updateQueue.value.shift()
        // console.log('[AgGrid] Removed item from queue, remaining items:', updateQueue.value.length)
      }
    }
  } catch (error) {
    console.error('[AgGrid] Error processing update queue:', error)
  } finally {
    if (!isActiveEditing.value) {
      isProcessingUpdateQueue.value = false
      // console.log('[AgGrid] Released update queue processing lock')
    } else {
      // console.log('[AgGrid] Keeping update queue processing lock until editing is complete')
    }
  }
}, { deep: true })

// Monitor editing state
const startEditWatcher = () => {
  if (!gridApi.value) return
  
  const editingInterval = setInterval(() => {
    if (gridApi.value) {
      try {
        const editingCells = gridApi.value.getEditingCells()
        const isEditing = editingCells && editingCells.length > 0
        isActiveEditing.value = isEditing
        
        if (isEditing) {
          // console.log('[AgGrid] Cell editing in progress, will defer async operations')
        }
      } catch (error) {
        console.warn('[AgGrid] Error checking editing cells:', error)
        isActiveEditing.value = false
      }
    } else {
      clearInterval(editingInterval)
    }
  }, 100)
  
  onUnmounted(() => {
    clearInterval(editingInterval)
  })
}

// Function to emit validation events to multiple tools (from PlantGrid.vue) 
// OPTIMIZATION: Reduced delay since external API transformers now apply results immediately
const emitValidationToTools = useDebounceFn((validationData: any, toolsToValidate: string[]) => {
  clearValidationCache()
  
  // Skip validation if "review on change" is disabled
  if (!reviewOnChange.value) {
    // console.log('[AgGrid Validation] Skipping validation - review on change is disabled')
    return
  }
  
  // console.log('[AgGrid Validation] Emitting validation events (after external API transformers applied results):', toolsToValidate)
  
  // Process validation with minimal delay since external API transformers now apply results immediately
  setTimeout(() => {
    toolsToValidate.forEach(toolUid => {
      try {
        mitter.emit(`panel:data:validate`, {
          ...validationData,
          validation_tool_uid: toolUid
        })
      } catch (error) {
        console.warn(`[AgGrid Validation] Error emitting validation to ${toolUid}:`, error)
      }
    })
  }, 50) // Reduced delay since external API transformers now apply results immediately
}, 800) // Reduced from 1500ms to 800ms since external API transformers now apply results immediately

// Handle incoming data from other panels
const handleIncomingPanelData = (incomingData: { sourcePanelId: string; sourceToolId: string; data: any }) => {
  try {
    // console.log('[AgGrid] Processing incoming panel data:', incomingData)
    
    // Call user-provided callback if available
    if (props.onDataReceived) {
      props.onDataReceived(incomingData)
    }
    
    // Check if the incoming data contains grid data that we should integrate
    if (incomingData.data && typeof incomingData.data === 'object') {
      const { data } = incomingData.data
      
      // Handle direct grid data format (from other grid components)
      if (data?.type === 'grid' && data?.rowData) {
        // console.log('[AgGrid] Processing direct grid data')
        
        // Convert string botanical/common names to object format like PlantGrid does
        data.rowData.forEach((row: any) => {
          if (row.botanicalname && typeof row.botanicalname === 'string') {
            row.botanicalname = {
              label: row.botanicalname,
              value: row.botanicalname
            }
          }
          if (row.commonname && typeof row.commonname === 'string') {
            row.commonname = {
              label: row.commonname,
              value: row.commonname
            }
          }
        })
        
        // console.log('[AgGrid] Updating data with direct grid format')
        handleDataUpdate([...data.rowData])
        
        // Update panel data to keep it in sync
        if (props.panel?.data) {
          props.panel.data.rowData = [...data.rowData]
          if (data.columnDefs) {
            props.panel.data.columnDefs = data.columnDefs
          }
        }
        return
      }
      
      // Handle file analysis data with JSON format
      if (data?.json && Array.isArray(data.json)) {
        // console.log('[AgGrid] Processing file analysis data')
        handleDataUpdate(data.json)
        
        // Update panel data to keep it in sync
        if (props.panel?.data) {
          props.panel.data.rowData = [...data.json]
        }
        return
      }
      
      // Try to parse as analysis result using PlantGrid parser
      if (data?.model || data?.parser) {
        const parser = data?.model?.toLowerCase().includes('gemini') ? 'gemini' : 'llamaparse'
        const parsedData = parse(data, parser)
        
        if (parsedData && parsedData.rowData) {
          // console.log('[AgGrid] Processing parsed analysis data')
          handleDataUpdate([...parsedData.rowData])
          
          // Update panel data to keep it in sync
          if (props.panel?.data) {
            props.panel.data.rowData = [...parsedData.rowData]
            if (parsedData.columnDefs) {
              props.panel.data.columnDefs = parsedData.columnDefs
            }
          }
          return
        }
      }
      
      // Handle other data types based on source tool
      switch (incomingData.sourceToolId) {
        case 'file-analysis':
          // Handle file analysis results
          if (data?.json && Array.isArray(data.json)) {
            // console.log('[AgGrid] Received file analysis data')
            handleDataUpdate(data.json)
            return
          }
          break
        case 'plant-grid':
        case 'plantgrid':
        case 'plantgridtwo':
          // Handle plant grid data
          if (data?.gridData && Array.isArray(data.gridData)) {
            // console.log('[AgGrid] Received plant grid data')
            handleDataUpdate(data.gridData)
            return
          }
          // Also try direct rowData format
          if (data?.rowData && Array.isArray(data.rowData)) {
            // console.log('[AgGrid] Received plant grid rowData')
            handleDataUpdate(data.rowData)
            return
          }
          break
        default:
          // console.log('[AgGrid] Received data from unknown tool type:', incomingData.sourceToolId)
      }
      
      // Handle generic grid data
      if (data?.gridData && Array.isArray(data.gridData)) {
        // console.log('[AgGrid] Processing generic grid data')
        handleDataUpdate(data.gridData)
        return
      }
      
      // Fallback: if we have an array at the top level, try to use it
      if (Array.isArray(incomingData.data)) {
        // console.log('[AgGrid] Using top-level array data')
        handleDataUpdate(incomingData.data)
        return
      }
    }
    
    // For backward compatibility, still emit the event
    emit('dataUpdate', [])
    
  } catch (error) {
    console.error('[AgGrid] Error processing incoming panel data:', error)
  }
}

// Auto-connect to default panel connections
const autoConnectToDefaults = () => {
  if (!effectiveAutoConnect.value || !panelConnectionsRef.value) return
  
  try {
    // Access the available connection panels and auto-connect to defaults
    const availablePanels = panelConnectionsRef.value.availableConnectionPanels?.value || []
    const defaultPanels = availablePanels.filter((panel: any) => panel.isDefault)
    
    // console.log('[AgGrid] Auto-connecting to default panels:', defaultPanels.length)
    
    defaultPanels.forEach((panel: any) => {
      if (!panel.isConnected) {
        // console.log('[AgGrid] Auto-connecting to:', panel.label)
        panelConnectionsRef.value.connect(panel.id)
      }
    })
  } catch (error) {
    console.warn('[AgGrid] Error during auto-connect:', error)
  }
}

// Initialize panel connections when available
const initializePanelConnections = () => {
  if (effectivePanelEventsEnabled.value && props.panel && import.meta.client) {
    try {
      const layoutStore = useLayoutStore()
      if (layoutStore?.layoutInstance?.value) {
        layoutStoreRef.value = layoutStore
        const panelConnections = usePanelConnections((props.panel as any).name)
        panelConnectionsRef.value = panelConnections
        
        // Register panel for data management
        registerPanel((props.panel as any).name, props.panel.data)
        
        // Setup incoming data handler if enabled
        if (effectiveHandleIncomingData.value) {
          panelConnections.handlePanelDataEvent((incomingData) => {
            // console.log('[AgGrid] Received data from panel:', incomingData)
            handleIncomingPanelData(incomingData)
          })
        }
        
        // console.log('[AgGrid] Panel connections initialized')
      }
    } catch (error) {
      console.warn('[AgGrid] Could not initialize panel connections:', error)
    }
  }
}

// Debounced function to emit grid data to other panels  
const emitGridData = useDebounceFn(async () => {
  if (!effectivePanelEventsEnabled.value || !props.panel || isEmittingData.value) return
  
  // console.log('[AgGrid] Emitting grid data to panels')
  isEmittingData.value = true
  
  try {
    // OPTIMIZATION: Only wait for cell response system processing since transformers now run independently
    // External API transformers apply their results immediately, so no need to wait for them
    if (cellResponseSystem.queueStatus.value.isProcessing) {
      // console.log('[AgGrid] Waiting for cell response system to complete before emitting data')
      // Schedule retry after critical operations complete
      setTimeout(() => {
        isEmittingData.value = false
        emitGridData()
      }, 100)
      return
    }
    
    const currentData = dataSource.value || []
    
    // Create data package with enhanced metadata
    const dataPackage = {
      tool_uid: toolId.value,
      panelId: (props.panel as any).name,
      gridData: currentData.map((row: any) => ({ ...row })),
      metadata: {
        timestamp: new Date().toISOString(),
        rowCount: currentData.length,
        source: 'agrid-component',
        version: '2.0'
      }
    }
    
    console.log('[AgGrid] Emitting data package:', {
      toolId: toolId.value,
      panelId: (props.panel as any).name,
      rowCount: currentData.length
    })
    
    // Emit validation events if validation tools are configured and review on change is enabled
    if (effectiveValidationTools.value?.length && reviewOnChange.value) {
      // console.log('[AgGrid] Emitting validation events to tools:', effectiveValidationTools.value)
      const validationData = {
        panelId: (props.panel as any).name,
        tool_uid: toolId.value,
        data: {
          rowData: currentData.map(row => {
            const normalizedRow: Record<string, any> = {}
            Object.entries(row).forEach(([key, val]) => {
              if (typeof val === 'object' && val !== null && 'value' in val) {
                normalizedRow[key] = (val as any).value
              } else {
                normalizedRow[key] = val
              }
              
              if (key === 'botanicalname' && typeof val === 'object' && val !== null) {
                normalizedRow[key] = (val as any).label || (val as any).value || ''
                if ((val as any).genus) normalizedRow.genus = (val as any).genus
                if ((val as any).family) normalizedRow.family = (val as any).family
              }
            })
            return normalizedRow
          }),
          columnDefs: columnsSource.value
        }
      }
      
      emitValidationToTools(validationData, effectiveValidationTools.value)
    } else if (effectiveValidationTools.value?.length && !reviewOnChange.value) {
      // console.log('[AgGrid] Skipping validation - review on change is enabled')
    }
    
    // Emit to connected panels via panel connections
    if (panelConnectionsRef.value?.emitData) {
      try {
        // Use simple emitData without complex panel iteration to avoid type issues
        panelConnectionsRef.value.emitData('grid', dataPackage)
        // console.log('[AgGrid] Emitted data via panel connections')
      } catch (error) {
        console.warn('[AgGrid] Error in panel data emission:', error)
      }
    }
    
    // Emit direct panel:toolData event for diagnostic/monitoring panels
    if (effectiveEmitPanelEvents.value) {
      mitter.emit('panel:toolData', {
        panelId: (props.panel as any).name,
        toolId: toolId.value,
        data: dataPackage
      })
      // console.log('[AgGrid] Emitted panel:toolData event')
    }
    
  } catch (error) {
    console.error('[AgGrid] Error emitting grid data:', error)
  } finally {
    // Release lock with delay to prevent rapid re-emission
    setTimeout(() => {
      isEmittingData.value = false
    }, 500)
  }
}, 500) // Debounce delay to prevent spam


// Create columns source computed property
const columnsSource = computed({
  get: () => {
    if (props.enablePanelPersistence && props.panel?.data?.columnDefs) {
      return props.panel.data.columnDefs
    }
    return props.columns
  },
  set: (newValue) => {
    if (props.enablePanelPersistence && props.panel) {
      if (!props.panel.data) {
        props.panel.data = { tool_uid: toolId.value }
      }
      // Cast to any to handle type compatibility
      props.panel.data.columnDefs = newValue as any
    }
  }
})

// Column type configurations for special handling
const getColumnTypeConfig = (field: string, schemaProperty: any) => {
  const fieldLower = field.toLowerCase()
  
  // Define column type configurations
  const columnConfigs = {
    // Botanical name column - wide, pinned, with special styling
    botanicalname: {
      minWidth: 250,
      suppressSizeToFit: true,
      // pinned: 'left' as const,
      cellRenderer: 'BotanicalNameCell',
      type: 'textColumn',
      // Extract display value for editing
      valueGetter: (params: any) => {
        if (params.data && params.data[params.colDef.field] !== undefined) {
          const value = params.data[params.colDef.field]
          if (typeof value === 'object' && value !== null) {
            return value.label || value.value || ''
          }
          return value || ''
        }
        return ''
      },
      // Format display value
      valueFormatter: (params: any) => {
        if (params.value === null || params.value === undefined) return ''
        if (typeof params.value === 'object' && params.value !== null) {
          return params.value.label || params.value.value || ''
        }
        return params.value
      },
      // Parse user input back to object format
      valueParser: (params: any) => {
        const oldValue = params.data[params.column.colId]
        if (typeof params.newValue === 'object' && params.newValue !== null) {
          return {
            label: params.newValue?.label || '',
            value: params.newValue?.value || params.newValue?.label || '',
            source: params.newValue?.source || oldValue?.source || 'user-edit',
            updatedAt: new Date().toISOString(),
            genus: params.newValue?.genus || oldValue?.genus || '',
            family: params.newValue?.family || oldValue?.family || ''
          }
        }
        return {
          label: params.newValue || '',
          value: params.newValue || '',
          source: oldValue?.source || 'user-edit',
          updatedAt: new Date().toISOString(),
          genus: oldValue?.genus || '',
          family: oldValue?.family || ''
        }
      },
      equals: (valueA: any, valueB: any) => {
        if (typeof valueA === 'object' && valueA !== null && typeof valueB === 'object' && valueB !== null) {
          return valueA.label === valueB.label && valueA.value === valueB.value
        }
        if (typeof valueA === 'object' && valueA !== null) {
          return valueA.label === valueB || valueA.value === valueB
        }
        if (typeof valueB === 'object' && valueB !== null) {
          return valueA === valueB.label || valueA === valueB.value
        }
        return valueA === valueB
      }
    },
    
    // Common name column - fixed width, italic styling
    commonname: {
      minWidth: 180,
      suppressSizeToFit: true,
      cellRenderer: 'CommonNameCell',
      type: 'textColumn',
      showLoadingIndicator: true, // Enable loading indicator for common name
      // Extract display value for editing
      valueGetter: (params: any) => {
        if (params.data && params.data[params.colDef.field] !== undefined) {
          const value = params.data[params.colDef.field]
          if (typeof value === 'object' && value !== null) {
            return value.label || value.value || ''
          }
          return value || ''
        }
        return ''
      },
      // Format display value
      valueFormatter: (params: any) => {
        if (params.value === null || params.value === undefined) return ''
        if (typeof params.value === 'object' && params.value !== null) {
          return params.value.label || params.value.value || ''
        }
        return params.value
      },
      // Parse user input back to object format
      valueParser: (params: any) => {
        const oldValue = params.data[params.column.colId]
        if (typeof params.newValue === 'object') {
          return {
            label: params.newValue?.label || params.newValue?.value || '',
            value: params.newValue?.value || params.newValue?.label || '',
            source: params.newValue?.source || oldValue?.source || 'user-edit',
            updatedAt: new Date().toISOString(),
            genus: params.newValue?.genus || oldValue?.genus || '',
            family: params.newValue?.family || oldValue?.family || ''
          }
        }
        return {
          label: params.newValue || '',
          value: params.newValue || '',
          source: oldValue?.source || 'user-edit',
          updatedAt: new Date().toISOString(),
          genus: oldValue?.genus || '',
          family: oldValue?.family || ''
        }
      },
      equals: (valueA: any, valueB: any) => {
        if (typeof valueA === 'object' && valueA !== null && typeof valueB === 'object' && valueB !== null) {
          return valueA.label === valueB.label && valueA.value === valueB.value && valueA.updatedAt === valueB.updatedAt
        }
        if (typeof valueA === 'object' && valueA !== null) {
          return valueA.label === valueB || valueA.value === valueB
        }
        if (typeof valueB === 'object' && valueB !== null) {
          return valueA === valueB.label || valueA === valueB.value
        }
        return valueA === valueB
      }
    },
    
    // Code column - narrow, centered, with badge rendering
    code: {
      minWidth: 90,
      maxWidth: 120,
      cellRenderer: 'CodeCell',
      type: 'codeColumn',
      // Extract display value for editing
      valueGetter: (params: any) => {
        if (params.data && params.data[params.colDef.field] !== undefined) {
          const value = params.data[params.colDef.field]
          if (typeof value === 'object' && value !== null) {
            return value.value !== undefined ? value.value : ''
          }
          return value === "null" ? '' : (value || '')
        }
        return ''
      },
      // Format display value
      valueFormatter: (params: any) => {
        if (params.value === null || params.value === undefined) return ''
        if (typeof params.value === 'object' && params.value !== null) {
          return params.value.value !== undefined ? params.value.value : ''
        }
        if (params.value === "null") return ''
        return params.value
      },
      // Parse user input back to object format
      valueParser: (params: any) => {
        const oldValue = params.data[params.column.colId]
        const metadata = (typeof oldValue === 'object' && oldValue !== null) 
          ? { ...oldValue, source: oldValue.source || 'user-edit' } 
          : { source: 'user-edit' }
        return { 
          ...metadata,
          value: params.newValue,
          updatedAt: new Date().toISOString()
        }
      },
      equals: (valueA: any, valueB: any) => {
        if (typeof valueA === 'object' && valueA !== null && typeof valueB === 'object' && valueB !== null) {
          return valueA.value === valueB.value && valueA.updatedAt === valueB.updatedAt
        }
        if (typeof valueA === 'object' && valueA !== null) {
          return valueA.value === valueB
        }
        if (typeof valueB === 'object' && valueB !== null) {
          return valueA === valueB.value
        }
        return valueA === valueB
      }
    },
    
    // Measurement columns (width, height, etc.)
    measurement: {
      minWidth: 80,
      type: 'numberColumn',
      cellRenderer: 'MeasurementCell',
      showLoadingIndicator: true, // Default to true for measurement columns
      // Extract display value for editing (without units)
      valueGetter: (params: any) => {
        if (params.data && params.data[params.colDef.field] !== undefined) {
          const value = params.data[params.colDef.field]
          const effectiveValue = typeof value === 'object' && value !== null
            ? value.value
            : value
          if (effectiveValue === null || effectiveValue === undefined || effectiveValue === '') return ''
          const numValue = Number(effectiveValue)
          return !isNaN(numValue) ? numValue : effectiveValue
        }
        return ''
      },
      // Format display value (with units)
      valueFormatter: (params: any) => {
        if (params.value === null || params.value === undefined) return ''
        const effectiveValue = typeof params.value === 'object' && params.value !== null
          ? params.value.value
          : params.value
        if (effectiveValue === null || effectiveValue === undefined || effectiveValue === '') return ''
        const numValue = Number(effectiveValue)
        return !isNaN(numValue) ? `${numValue.toFixed(1)}m` : String(effectiveValue)
      },
      // Parse user input back to object format
      valueParser: (params: any) => {
        const oldValue = params.data[params.column.colId]
        const metadata = (typeof oldValue === 'object' && oldValue !== null) 
          ? { ...oldValue, source: oldValue.source || 'user-edit' } 
          : { source: 'user-edit' }
        
        let newValue = null
        if (params.newValue !== null && params.newValue !== undefined && params.newValue !== '') {
          const numValue = Number(params.newValue.toString().replace(/[^\d.-]/g, ''))
          if (!isNaN(numValue)) {
            newValue = Number(numValue.toFixed(2))
          }
        }
        
        return { 
          ...metadata,
          value: newValue,
          updatedAt: new Date().toISOString()
        }
      },
      equals: (valueA: any, valueB: any) => {
        if (typeof valueA === 'object' && valueA !== null && typeof valueB === 'object' && valueB !== null) {
          return valueA.value === valueB.value && valueA.updatedAt === valueB.updatedAt
        }
        if (typeof valueA === 'object' && valueA !== null) {
          return valueA.value === valueB
        }
        if (typeof valueB === 'object' && valueB !== null) {
          return valueA === valueB.value
        }
        return valueA === valueB
      }
    }
  }
  
  // Determine which config to use
  if (fieldLower === 'botanicalname') return columnConfigs.botanicalname
  if (fieldLower === 'commonname') return columnConfigs.commonname  
  if (fieldLower === 'code') return columnConfigs.code
  if (['maturewidth', 'width', 'height', 'matureheight', 'spread'].includes(fieldLower)) {
    const measurementConfig = { ...columnConfigs.measurement }
    
    // Disable loading indicators for basic width/height, keep for mature dimensions
    if (fieldLower === 'width' || fieldLower === 'height') {
      measurementConfig.showLoadingIndicator = false
    }
    
    return measurementConfig
  }
  
  // Default configuration
  return {
    minWidth: 50,
    type: schemaProperty?.type === 'number' ? 'numberColumn' : 'textColumn',
    cellRenderer: 'DefaultCell',
    // Extract display value for editing
    valueGetter: (params: any) => {
      if (params.data && params.data[params.colDef.field] !== undefined) {
        const value = params.data[params.colDef.field]
        const effectiveValue = typeof value === 'object' && value !== null
          ? value.value
          : value
        return String(effectiveValue || '')
      }
      return ''
    },
    // Format display value
    valueFormatter: (params: any) => {
      if (params.value === null || params.value === undefined) return ''
      const effectiveValue = typeof params.value === 'object' && params.value !== null
        ? params.value.value
        : params.value
      return String(effectiveValue || '')
    },
    // Parse user input back to object format
    valueParser: (params: any) => {
      const oldValue = params.data[params.column.colId]
      const metadata = (typeof oldValue === 'object' && oldValue !== null) 
        ? { ...oldValue, source: oldValue.source || 'user-edit' } 
        : { source: 'user-edit' }
      return { 
        ...metadata,
        value: params.newValue,
        updatedAt: new Date().toISOString()
      }
    }
  }
}

// Schema-based column generation
const generateColumnsFromSchema = (schema: any): ColumnDef[] => {
  if (!schema?.properties) return []
  
  const columns: ColumnDef[] = []
  
  // Add a boolean checkbox column if checkbox selection is enabled
  if (props.checkboxSelection) {
    columns.unshift({
      headerName: '',
      field: 'selected',
      cellDataType: 'boolean',
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      editable: true,
      width: 80,
      minWidth: 80,
      maxWidth: 40,
      pinned: 'left',
      lockPosition: true,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
      suppressMovable: true,
      sortable: false,
      filter: false,
      filterable: false,
      resizable: false,
      suppressMenu: true,
      suppressFiltersToolPanel: true,
      suppressColumnsToolPanel: true,
      // Explicitly disable tooltips for checkbox column
      suppressTooltips: true,
      tooltipComponent: undefined,
      headerTooltip: undefined,
      tooltipValueGetter: undefined
    } as ColumnDef)
  }
  
  // Generate columns from schema properties
  Object.entries(schema.properties).forEach(([key, value]: [string, any]) => {
    const typeConfig = getColumnTypeConfig(key, value)
    
    const column: ColumnDef = {
      field: key,
      headerName: columnDisplayLabels[key] || key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
      editable: true,
      sortable: true,
      filter: true,
      suppressHeaderFilterButton: true,
      suppressHeaderMenuButton: true,
      suppressFiltersToolPanel: true,
      suppressColumnsToolPanel: true,
      resizable: true,
      // Enhanced tooltip configuration using custom Vue component
      tooltipComponent: 'CustomTooltip',
      headerTooltip: value.description,
      // Apply type-specific configuration
      ...typeConfig
    }
    
    // Add autocomplete configuration if present in schema property OR autocompleteConfigs prop
    if (value.autocomplete) {
      (column as any).autocomplete = value.autocomplete
    } else if (props.autocompleteConfigs[key]) {
      (column as any).autocomplete = props.autocompleteConfigs[key]
    }
    
    columns.push(column)
  })
  
  return columns
}

// Initialize internal row data
watch(() => props.data, (newData) => {
  if (newData) {
    internalRowData.value = [...newData]
    if (gridApi.value) {
      // Fix AG Grid Error #252: Wrap setGridOption calls in setTimeout
      setTimeout(() => {
        if (gridApi.value) {
          gridApi.value.setGridOption('rowData', internalRowData.value)
        }
      }, 0)
    }
  }
}, { deep: true })

// Initialize internal row data
watch(() => dataSource.value, (newData) => {
  if (newData) {
    internalRowData.value = [...newData]
    if (gridApi.value) {
      // Fix AG Grid Error #252: Wrap setGridOption calls in setTimeout
      // Use nextTick to ensure Vue has finished updating before calling AG Grid
      nextTick(() => {
        setTimeout(() => {
          if (gridApi.value) {
            gridApi.value.setGridOption('rowData', internalRowData.value)
          }
        }, 0)
      })
    }
  }
}, { deep: true, immediate: true })

// Create empty row from schema
const createEmptyRowFromSchema = (schema: any): TableRow => {
  if (!schema?.properties) return {}
  
  const emptyRow: TableRow = {}
  
  // Add selected field for checkbox if enabled
  if (props.checkboxSelection) {
    emptyRow.selected = false
  }
  
  Object.keys(schema.properties).forEach(key => {
    emptyRow[key] = null
  })
  
  return emptyRow
}

// Theme parameters (add right after dataSource computed property)
const BASE_FONT_SIZE = 14
const BASE_ROW_HEIGHT = 48
const BASE_HEADER_HEIGHT = 48

const themeParams = ref({
  fontSize: BASE_FONT_SIZE,
  headerFontSize: BASE_FONT_SIZE + 2,
  rowHeight: BASE_ROW_HEIGHT,
  headerHeight: BASE_HEADER_HEIGHT,
  fontFamily: 'InterVariable, sans-serif',
  cellHorizontalPadding: 12,
  cellVerticalPadding: 16,
  rangeSelectionBorderStyle: 'solid' as const,
  rangeSelectionBorderColor: 'hsl(var(--primary) / 0.5)',
  selectedRowBackgroundColor: 'hsl(var(--primary) / 0.2)',
  accentColor: 'hsl(var(--muted))',
  backgroundColor: 'hsl(var(--background))',
  foregroundColor: 'hsl(var(--foreground))',
  headerBackgroundColor: 'hsl(var(--muted) / 0.5)',
  controlPanelBackgroundColor: 'hsl(var(--muted))',
  checkboxCheckedBackgroundColor: 'hsl(var(--primary))',
  rangeSelectionBackgroundColor: 'hsl(var(--muted) / 0.5)',
  cellEditingShadow: { offsetX: 0, offsetY: 0, radius: 0, spread: 0, color: 'rgba(0, 0, 0, 0)' },
  inputFocusShadow: { offsetX: 0, offsetY: 0, radius: 0, spread: 0, color: 'rgba(0, 0, 0, 0)' },
  rowHoverColor: 'hsl(var(--muted) / 0.5)',
  inputBorderRadius: 4,
  cellEditingBorderColor: 'hsl(var(--primary))',
  cellEditingBorder: { width: 2, color: 'hsl(var(--primary))', radius: 4 },
  inputBorder: { width: 2, color: 'hsl(var(--primary))', radius: 4 },
  inputFocusBorder: { width: 2, color: 'hsl(var(--primary))', radius: 4 }
})

// Create theme
const customTheme = computed(() => themeQuartz.withParams({
  fontFamily: themeParams.value.fontFamily,
  cellHorizontalPadding: themeParams.value.cellHorizontalPadding,
  rangeSelectionBorderStyle: themeParams.value.rangeSelectionBorderStyle as 'solid',
  rangeSelectionBorderColor: themeParams.value.rangeSelectionBorderColor,
  rangeSelectionBackgroundColor: themeParams.value.rangeSelectionBackgroundColor,
  cellEditingShadow: themeParams.value.cellEditingShadow,
  inputFocusShadow: themeParams.value.inputFocusShadow,
  selectedRowBackgroundColor: themeParams.value.selectedRowBackgroundColor,
  accentColor: themeParams.value.accentColor,
  backgroundColor: themeParams.value.backgroundColor,
  foregroundColor: themeParams.value.foregroundColor,
  headerBackgroundColor: themeParams.value.headerBackgroundColor,
  checkboxCheckedBackgroundColor: themeParams.value.checkboxCheckedBackgroundColor,
  // Disable row hover when color schemes are active, otherwise use default
  rowHoverColor: hasActiveSchemes.value ? 'transparent' : themeParams.value.rowHoverColor,
  inputBorderRadius: themeParams.value.inputBorderRadius,
  cellEditingBorder: themeParams.value.cellEditingBorder
}))

// Grid style
const gridStyle = computed(() => ({
  width: '100%',
  height: '100%',
  '--ag-font-size': `${themeParams.value.fontSize}px`,
  '--ag-header-font-size': `${themeParams.value.headerFontSize}px`,
  '--ag-row-height': `${themeParams.value.rowHeight}px`,
  '--ag-header-height': `${themeParams.value.headerHeight}px`,
  '--ag-value-change-value-highlight-background-color': 'hsl(var(--primary) / 0.5)'
}))

// Column width adjustment function
const adjustColumnWidths = () => {
  if (!gridApi.value) return
  
  if (useFitContents.value) {
    gridApi.value.autoSizeAllColumns()
  } else {
    gridApi.value.sizeColumnsToFit()
  }
}

// Toggle sizing function
const handleToggleSizing = () => {
  useFitContents.value = !useFitContents.value
  if (gridApi.value) {
    adjustColumnWidths()
  }
}

// ... existing code ...

// Computed properties for grid configuration
const gridOptions = computed(() => ({
  pagination: props.pagination,
  paginationPageSize: props.pageSize,
  suppressRowClickSelection: props.checkboxSelection ? true : props.suppressRowClickSelection,
  rowSelection: props.checkboxSelection ? undefined : props.rowSelection,
  animateRows: props.animateRows,
  enableRangeSelection: false,
  suppressMenuHide: true,
  tooltipShowDelay: 300,
  tooltipHideDelay: 3000, // Increased from 1000ms to 3000ms (3 seconds)
  tooltipInteraction: true,
  tooltipMouseTrack: true,
  components,
  context: gridContext.value, // Pass loading state context to cell renderers
  undoRedoCellEditing: true,
  undoRedoCellEditingLimit: 20,
  // Add row styling for color application
  getRowStyle: rowStyle,
  onUndoStarted: () => console.log('Undo started'),
  onUndoEnded: (event: any) => {
    // console.log('Undo ended, operation performed:', event.operationPerformed)
    updateUndoRedoState()
  },
  onRedoStarted: () => console.log('Redo started'),
  onRedoEnded: (event: any) => {
    // console.log('Redo ended, operation performed:', event.operationPerformed)
    updateUndoRedoState()
  }
}))

const processedColumns = computed(() => {
  // Use schema-generated columns if schema is provided and no explicit columns
  let baseColumns: any[] = columnsSource.value.length > 0 
    ? columnsSource.value 
    : props.schema 
      ? generateColumnsFromSchema(props.schema)
      : []
  
  // Ensure checkbox column is always first if checkbox selection is enabled
  if (props.checkboxSelection) {
    // Check if checkbox column already exists
    const hasCheckboxColumn = baseColumns.some(col => col.field === 'selected')
    
    if (!hasCheckboxColumn) {
      // Add checkbox column at the beginning
      const checkboxColumn = {
        headerName: '',
        field: 'selected',
        cellDataType: 'boolean',
        cellRenderer: 'agCheckboxCellRenderer',
        cellEditor: 'agCheckboxCellEditor',
        editable: true,
        width: 80,
        minWidth: 80,
        maxWidth: 40,
        pinned: 'left' as const,
        lockPosition: true,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        suppressMovable: true,
        sortable: false,
        filter: false,
        filterable: false,
        resizable: false,
        suppressMenu: true,
        suppressFiltersToolPanel: true,
        suppressColumnsToolPanel: true,
        // Explicitly disable tooltips for checkbox column
        suppressTooltips: true,
        tooltipComponent: undefined,
        headerTooltip: undefined,
        tooltipValueGetter: undefined
      }
      baseColumns = [checkboxColumn, ...baseColumns]
    } else {
      // If checkbox column exists but not first, move it to first position
      const checkboxIndex = baseColumns.findIndex(col => col.field === 'selected')
      if (checkboxIndex > 0) {
        const checkboxColumn = baseColumns.splice(checkboxIndex, 1)[0]
        baseColumns.unshift(checkboxColumn)
      }
    }
  }
      
  return baseColumns.map((col: any) => {
    const processedCol: any = {
      ...col,
      sortable: col.sortable !== undefined ? col.sortable : props.enableSorting,
      filter: col.filterable !== undefined ? col.filterable : props.enableFiltering,
      resizable: col.resizable !== undefined ? col.resizable : props.enableColumnResize,
      // Enhanced tooltip configuration
      tooltipComponent: 'CustomTooltip',
      tooltipField: col.tooltipField || col.field, // Fallback to showing field value
      tooltipValueGetter: col.tooltipValueGetter || ((params: any) => {
        // console.log('[Tooltip Debug] Tooltip triggered for field:', params.colDef.field, 'value:', params.value)
        return params.value
      }),
      suppressHeaderMenuButton: col.suppressHeaderMenuButton !== undefined ? col.suppressHeaderMenuButton : true,
      // Add cell styling for color application
      cellStyle: col.cellStyle || cellStyle,
    }
    
    // Add autocomplete functionality if enabled and configured
    const autocompleteConfig = col.autocomplete || props.autocompleteConfigs[col.field]
    if (props.enableAutocomplete && autocompleteConfig && autocompleteLoaded.value) {
      processedCol.cellEditor = CustomEscapeAwareEditor || AutocompleteSelectCellEditor
      processedCol.cellEditorParams = {
        autocomplete: {
          ...autocompleteConfig,
          // Ensure free text is allowed to prevent external API calls for partial entries
          strict: autocompleteConfig.strict !== undefined ? autocompleteConfig.strict : false
        },
        placeholder: col.placeholder || 'Search by anything...'
      }
      
      // Default value parser and formatter for autocomplete
      if (!processedCol.valueParser) {
        processedCol.valueParser = (params: any) => {
          if (typeof params.newValue === 'object') {
            return params.newValue?.label || params.newValue?.value || ''
          }
          return params.newValue || ''
        }
      }
      
      if (!processedCol.valueFormatter) {
        processedCol.valueFormatter = (params: any) => {
          if (typeof params.value === 'object') {
            return params.value?.label || params.value?.value || ''
          }
          return params.value || ''
        }
      }
    }
    
    return processedCol
  })
})

const processedDefaultColDef = computed(() => ({
  sortable: props.enableSorting,
  filter: props.enableFiltering,
  resizable: props.enableColumnResize,
  editable: false,
  cellStyle: { fontSize: '14px' },
  headerHeight: 40,
  // Add tooltip configuration for all columns by default
  tooltipComponent: 'CustomTooltip',
  // Add debug logging for tooltips
  tooltipValueGetter: (params: any) => {
    // console.log('[Default Tooltip Debug] Showing tooltip for:', params.colDef.field, 'value:', params.value)
    return `${params.colDef.headerName || params.colDef.field}: ${params.value}`
  },
  ...props.defaultColDef
}))

// Grid event handlers
const onGridReady = (params: GridReadyEvent) => {
  gridApi.value = params.api
  
  // Initialize undo/redo state
  updateUndoRedoState()
  
  // Start watching for edit operations (from PlantGrid.vue)
  if (effectiveEnableUpdateQueue.value) {
    startEditWatcher()
  }
  
  // Set up cell response system if enabled (transformers or cell responses)
  if (props.enableTransformers || props.enableCellResponses) {
    // console.log('[AgGrid] Setting up cell response system with transformers enabled:', props.enableTransformers)
    cellResponseSystem.setupGrid(params.api)
    
    // Register rules from props
    if (props.cellResponseRules) {
      props.cellResponseRules.forEach(rule => {
        cellResponseSystem.registerRule(rule)
      })
    }
    
    // The botanical cell response system auto-registers rules when transformers are enabled
    if (props.enableTransformers) {
      // console.log('[AgGrid] Botanical transformations will be auto-registered by useBotanicalCellResponseSystem')
    }
  }
  
  // Always start with 1 row if there's no existing data (matching PlantGrid.vue behavior)
  if (!internalRowData.value || internalRowData.value.length === 0) {
    const emptyRow = props.schema 
      ? createEmptyRowFromSchema(props.schema)
      : { ...(props.checkboxSelection ? { selected: false } : {}) }
    
    internalRowData.value = [emptyRow]
    // Fix AG Grid Error #252: Wrap setGridOption calls in setTimeout
    setTimeout(() => {
      params.api.setGridOption('rowData', internalRowData.value)
    }, 0)
  } else if (internalRowData.value && internalRowData.value.length > 0) {
    // Use existing internal data
    // Fix AG Grid Error #252: Wrap setGridOption calls in setTimeout
    setTimeout(() => {
      params.api.setGridOption('rowData', internalRowData.value)
    }, 0)
  }
  
  // Auto-focus first cell if enabled - prioritize botanicalname like PlantGrid.vue
  if (props.autoFocusFirstCell) {
    setTimeout(() => {
      const firstRow = params.api.getDisplayedRowAtIndex(0)
      if (firstRow) {
        // First try to find 'botanicalname' column (matching PlantGrid.vue behavior)
        let targetColumn = processedColumns.value.find(col => 
          col.field === 'botanicalname' && col.editable !== false
        )
        
        // If no botanicalname column, fall back to first editable column
        if (!targetColumn) {
          targetColumn = processedColumns.value.find(col => 
            col.editable !== false && col.field !== 'selected'
          )
        }
        
        if (targetColumn) {
          // Check if the target cell has a value (using PlantGrid.vue logic)
          const cellValue = firstRow.data[targetColumn.field]
          const hasValue = cellValue && 
                          (typeof cellValue === 'string' ? 
                            cellValue.trim() !== '' : 
                            (cellValue?.label ? cellValue.label.trim() !== '' : 
                             cellValue?.value ? String(cellValue.value).trim() !== '' : false))
          
          // Only start editing if the cell is empty
          if (!hasValue) {
            // Fix AG Grid Error #252: Extra safety for startEditingCell
            setTimeout(() => {
              params.api.startEditingCell({
                rowIndex: 0,
                colKey: targetColumn.field
              })
            }, 50)
          }
        }
      }
    }, 200)
  }
  
  if (props.autoSizeColumns) {
    setTimeout(() => {
      autoSizeAllColumns()
    }, 150)
  }
  
  // Update floating button position after grid is ready
  setTimeout(updateFloatingButtonPosition, 200)
  
  // Find the quantity column header for help video targeting
  setTimeout(() => {
    findQuantityColumnHeader()
  }, 300)
  
  // Calculate data ranges for color normalization
  setTimeout(() => {
    calculateDataRanges()
  }, 500)
  
  emit('ready', params.api)
}

const onCellValueChanged = (event: CellValueChangedEvent) => {
  // Skip updates if we're handling an external update to prevent circular loops
  if (isHandlingExternalUpdate.value) {
    // console.log('[AgGrid] Skipping cell value changed - handling external update')
    return
  }
  
  // Update internal row data
  if (event.rowIndex !== null && event.rowIndex < internalRowData.value.length) {
    const updatedRow = { ...internalRowData.value[event.rowIndex] }
    if (event.colDef.field) {
      updatedRow[event.colDef.field] = event.newValue
    }
    internalRowData.value[event.rowIndex] = updatedRow
    
    // Emit data update and sync with panel if persistence is enabled
    const updatedData = [...internalRowData.value]
    dataSource.value = updatedData
    emit('dataUpdate', updatedData)
  }
  
  // If the selected field changed, trigger selection change
  if (event.colDef.field === 'selected') {
    onSelectionChanged()
  }
  
  // Process cell response rules if enabled (transformers or cell responses)
  if (props.enableTransformers || props.enableCellResponses) {
    // Check if this is a free text entry and pass that info to the cell response system
    const isFreeTextEntry = event.newValue && 
                           typeof event.newValue === 'object' && 
                           event.newValue.source === 'free-text-entry'
    
    // console.log('[AgGrid] Processing cell value change through cell response system:', {field: event.colDef?.field, newValue: event.newValue, isFreeTextEntry })
    
    cellResponseSystem.handleCellValueChanged(event)
  }
  
  // Update floating button position after cell changes
  setTimeout(updateFloatingButtonPosition, 50)
  
  emit('cellValueChanged', event)
}

const onRowSelected = (event: RowSelectedEvent) => {
  emit('rowSelected', event)
}

const onCellEditingStarted = (event: any) => {
  // Check if this is an autocomplete field
  const field = event.colDef?.field
  
  // Use the same reliable row identification as BotanicalNameCell
  let rowIdentifier = null
  if (event.rowIndex !== undefined && event.rowIndex !== null) {
    rowIdentifier = event.rowIndex
  } else if (event.node?.rowIndex !== undefined && event.node?.rowIndex !== null) {
    rowIdentifier = event.node.rowIndex
  } else if (event.node?.id !== undefined && event.node?.id !== null) {
    rowIdentifier = `node_${event.node.id}`
  } else {
    rowIdentifier = 'fallback'
  }
  
  if (field && rowIdentifier !== null && field === 'botanicalname') {
    const cellId = `${rowIdentifier}-${field}`
    
    // console.log('[AgGrid] Botanical name cell editing started - preparing for PRIORITY autocomplete requests')
    
    // OPTIMIZATION: Reduced delay for faster autocomplete initialization
    setTimeout(() => {
      // Check if the cell is still being edited and likely using autocomplete
      if (gridApi.value) {
        const editingCells = gridApi.value.getEditingCells()
        const isStillEditing = editingCells.some(cell => 
          (cell.rowIndex === rowIdentifier || cell.rowIndex === event.rowIndex) && 
          cell.column?.getColId() === field
        )
        
        if (isStillEditing) {
          // console.log('[AgGrid] Setting up autocomplete loading state for GBIF requests')
          // Update the context Map so cell renderers can see the loading state
          autocompleteLoadingCells.value.set(cellId, true)
          
          // Force AG Grid to refresh the cell to pick up context changes
          setTimeout(() => {
            if (gridApi.value && event.node && event.colDef?.field) {
              try {
                gridApi.value.refreshCells({
                  rowNodes: [event.node],
                  columns: [event.colDef.field],
                  force: true
                })
              } catch (error) {
                console.warn('[AgGrid] Error refreshing cell:', error)
              }
            }
          }, 0)
        }
      }
    }, 50) // OPTIMIZATION: Reduced from 200ms to 50ms for faster autocomplete initialization
  }
}

const onCellEditingStopped = (event: CellEditingStoppedEvent) => {
  // Handle autocomplete loading end
  const field = event.colDef?.field
  
  // Use the same reliable row identification
  let rowIdentifier = null
  if (event.rowIndex !== undefined && event.rowIndex !== null) {
    rowIdentifier = event.rowIndex
  } else if ((event as any).node?.rowIndex !== undefined && (event as any).node?.rowIndex !== null) {
    rowIdentifier = (event as any).node.rowIndex
  } else if ((event as any).node?.id !== undefined && (event as any).node?.id !== null) {
    rowIdentifier = `node_${(event as any).node.id}`
  } else {
    rowIdentifier = 'fallback'
  }
  
  if (field && rowIdentifier !== null && field === 'botanicalname') {
    const cellId = `${rowIdentifier}-${field}`
    
    // Clear loading state from context Map
    const wasLoading = autocompleteLoadingCells.value.has(cellId)
    autocompleteLoadingCells.value.delete(cellId)
    
    // Force AG Grid to refresh the cell to clear loading state
    if (wasLoading) {
      setTimeout(() => {
        if (gridApi.value && (event as any).node && event.colDef?.field) {
          try {
            gridApi.value.refreshCells({
              rowNodes: [(event as any).node],
              columns: [event.colDef.field],
              force: true
            })
          } catch (error) {
            console.warn('[AgGrid] Error refreshing cell on stop:', error)
          }
        }
      }, 0)
    }
  }
  
  // Process cell response system queue when editing stops
  if (props.enableTransformers || props.enableCellResponses) {
    // console.log('[AgGrid] Processing cell editing stopped through cell response system:', { field: event.colDef?.field, newValue: event.newValue })
    cellResponseSystem.handleCellEditingStopped(event)
  }
  
  // The cell response system automatically handles all transformations when enabled
  
  emit('cellEditingStopped', event)
}



// Helper function to apply transformer updates (extracted for reuse)
const applyTransformerUpdates = (event: CellEditingStoppedEvent, updates: Record<string, any>) => {
  if (!event.node || !gridApi.value) return
  
  // Apply updates to the grid node, preserving all metadata including attribution
  Object.entries(updates).forEach(([key, updateValue]) => {
    // Preserve the complete object structure including grounding metadata, source text, etc.
    const existingValue = event.node!.data[key]
    
    if (typeof updateValue === 'object' && updateValue !== null) {
      // Merge with existing data to preserve any additional metadata
      if (typeof existingValue === 'object' && existingValue !== null) {
        event.node!.data[key] = { 
          ...existingValue, 
          ...updateValue,
          source: updateValue.source || 'transformer',
          updatedAt: new Date().toISOString()
        }
      } else {
        event.node!.data[key] = {
          ...updateValue,
          source: updateValue.source || 'transformer',
          updatedAt: new Date().toISOString()
        }
      }
    } else {
      // Handle primitive values by preserving object structure
      if (typeof existingValue === 'object' && existingValue !== null) {
        event.node!.data[key] = {
          ...existingValue,
          value: updateValue,
          source: 'transformer',
          updatedAt: new Date().toISOString()
        }
      } else {
        event.node!.data[key] = updateValue
      }
    }
  })
  
  // Fix AG Grid Error #252: Wrap grid API calls in setTimeout to avoid calling during render stage
  setTimeout(() => {
    if (gridApi.value && event.node) {
      gridApi.value.refreshCells({
        rowNodes: [event.node],
        columns: Object.keys(updates),
        force: true
      })
    }
  }, 0)
  
  // Update internal row data
  const rowIndex = event.rowIndex
  if (rowIndex !== null && rowIndex >= 0 && rowIndex < internalRowData.value.length) {
    const updatedRow = { ...internalRowData.value[rowIndex] }
    
    // Apply updates to internal row data, preserving metadata
    Object.entries(updates).forEach(([key, updateValue]) => {
      const existingValue = updatedRow[key]
      
      if (typeof updateValue === 'object' && updateValue !== null) {
        if (typeof existingValue === 'object' && existingValue !== null) {
          updatedRow[key] = { 
            ...existingValue, 
            ...updateValue,
            source: updateValue.source || 'transformer',
            updatedAt: new Date().toISOString()
          }
        } else {
          updatedRow[key] = {
            ...updateValue,
            source: updateValue.source || 'transformer',
            updatedAt: new Date().toISOString()
          }
        }
      } else {
        // Handle primitive values by preserving object structure
        if (typeof existingValue === 'object' && existingValue !== null) {
          updatedRow[key] = {
            ...existingValue,
            value: updateValue,
            source: 'transformer',
            updatedAt: new Date().toISOString()
          }
        } else {
          updatedRow[key] = updateValue
        }
      }
    })
    
    internalRowData.value[rowIndex] = updatedRow
    
    // Sync with panel data and emit event
    const updatedData = [...internalRowData.value]
    dataSource.value = updatedData
    emit('dataUpdate', updatedData)
  }
}

const onSelectionChanged = () => {
  if (gridApi.value) {
    if (props.checkboxSelection) {
      // For cell-based checkboxes, count rows where selected = true
      const allRows: any[] = []
      gridApi.value.forEachNode(node => {
        if (node.data) {
          allRows.push(node.data)
        }
      })
      
      const selectedRows = allRows.filter(row => row.selected === true)
      selectedRowCount.value = selectedRows.length
      emit('selectionChanged', selectedRows)
    } else {
      // For row selection
      const selectedRows = gridApi.value.getSelectedRows()
      selectedRowCount.value = selectedRows.length
      emit('selectionChanged', selectedRows)
    }
  }
}

// Public methods
const addRow = (rowData?: TableRow, index?: number) => {
  if (!gridApi.value) return
  
  // Create empty row if no data provided
  const newRow = rowData || (props.schema 
    ? createEmptyRowFromSchema(props.schema)
    : { ...(props.checkboxSelection ? { selected: false } : {}) })
  
  // Add to internal data
  if (index !== undefined) {
    internalRowData.value.splice(index, 0, newRow)
  } else {
    internalRowData.value.push(newRow)
  }
  
  // Update grid
  // Fix AG Grid Error #252: Wrap setGridOption calls in setTimeout
  setTimeout(() => {
    if (gridApi.value) {
      gridApi.value.setGridOption('rowData', internalRowData.value)
    }
  }, 0)
  
  // Sync with panel data and emit event
  const updatedData = [...internalRowData.value]
  dataSource.value = updatedData
  emit('dataUpdate', updatedData)
}

const updateRow = (rowId: string | number, updates: Partial<TableRow>) => {
  if (!gridApi.value) return
  
  const rowNode = gridApi.value.getRowNode(rowId.toString())
  if (rowNode) {
    Object.assign(rowNode.data, updates)
    
    // Fix AG Grid Error #252: Wrap grid API calls in setTimeout to avoid calling during render stage
    setTimeout(() => {
      if (gridApi.value && rowNode) {
        gridApi.value.refreshCells({ rowNodes: [rowNode] })
      }
    }, 0)
    
    // Update internal data
    const index = internalRowData.value.findIndex((row, idx) => idx.toString() === rowId.toString())
    if (index !== -1) {
      Object.assign(internalRowData.value[index], updates)
      emit('dataUpdate', internalRowData.value)
    }
  }
}

const removeRow = (rowId: string | number) => {
  if (!gridApi.value) return
  
  const rowNode = gridApi.value.getRowNode(rowId.toString())
  if (rowNode) {
    // Remove from internal data
    const index = parseInt(rowId.toString())
    if (index >= 0 && index < internalRowData.value.length) {
      internalRowData.value.splice(index, 1)
    }
    
    // Update grid
    // Fix AG Grid Error #252: Wrap setGridOption calls in setTimeout
    setTimeout(() => {
      if (gridApi.value) {
        gridApi.value.setGridOption('rowData', internalRowData.value)
      }
    }, 0)
    
    // Emit events
    emit('dataUpdate', internalRowData.value)
    onSelectionChanged()
  }
}

const removeSelectedRows = () => {
  if (!gridApi.value) return
  
  let selectedRows: any[] = []
  let selectedIndices: number[] = []
  
  if (props.checkboxSelection) {
    // For cell-based checkboxes
    const allRows: any[] = []
    gridApi.value.forEachNode((node, index) => {
      if (node.data) {
        allRows.push({ data: node.data, index })
      }
    })
    
    const selected = allRows.filter(item => item.data.selected === true)
    selectedRows = selected.map(item => item.data)
    selectedIndices = selected.map(item => item.index)
  } else {
    // For row selection
    selectedRows = gridApi.value.getSelectedRows()
    // Get indices of selected rows
    const selectedNodes = gridApi.value.getSelectedNodes()
    selectedIndices = selectedNodes.map(node => node.rowIndex).filter(idx => idx !== null) as number[]
  }
  
  if (selectedRows.length > 0) {
    // Sort indices in descending order to remove from end first
    selectedIndices.sort((a, b) => b - a)
    
    // Remove from internal data
    selectedIndices.forEach(index => {
      if (index >= 0 && index < internalRowData.value.length) {
        internalRowData.value.splice(index, 1)
      }
    })
    
    // Update grid
    // Fix AG Grid Error #252: Wrap setGridOption calls in setTimeout
    setTimeout(() => {
      if (gridApi.value) {
        gridApi.value.setGridOption('rowData', internalRowData.value)
      }
    }, 0)
    
    // Sync with panel data and emit events
    const updatedData = [...internalRowData.value]
    dataSource.value = updatedData
    emit('deleteSelectedRows', selectedIndices)
    emit('dataUpdate', updatedData)
    onSelectionChanged()
  }
}

const getSelectedRows = () => {
  if (!gridApi.value) return []
  
  if (props.checkboxSelection) {
    const allRows: any[] = []
    gridApi.value.forEachNode(node => {
      if (node.data && node.data.selected === true) {
        allRows.push(node.data)
      }
    })
    return allRows
  } else {
    return gridApi.value.getSelectedRows()
  }
}

const clearSelection = () => {
  if (!gridApi.value) return
  
  if (props.checkboxSelection) {
    gridApi.value.forEachNode(node => {
      if (node.data && node.data.selected === true) {
        node.setDataValue('selected', false)
      }
    })
  } else {
    gridApi.value.deselectAll()
  }
  
  onSelectionChanged()
}

const selectAllRows = () => {
  if (!gridApi.value) return
  
  if (props.checkboxSelection) {
    gridApi.value.forEachNode(node => {
      if (node.data) {
        node.setDataValue('selected', true)
      }
    })
  } else {
    gridApi.value.selectAll()
  }
  
  onSelectionChanged()
}

const exportToCsv = (filename?: string) => {
  gridApi.value?.exportDataAsCsv({
    fileName: filename || 'table-data.csv'
  })
}

const autoSizeAllColumns = () => {
  if (gridApi.value) {
    gridApi.value.sizeColumnsToFit()
  }
}

const refreshData = () => {
  // Fix AG Grid Error #252: Wrap refreshCells calls in setTimeout
  setTimeout(() => {
    if (gridApi.value) {
      gridApi.value.refreshCells()
    }
  }, 0)
}

const setQuickFilter = (text: string) => {
  // Fix AG Grid Error #252: Wrap setGridOption calls in setTimeout
  setTimeout(() => {
    if (gridApi.value) {
      gridApi.value.setGridOption('quickFilterText', text)
    }
  }, 0)
}

const handleAddRow = () => {
  // Capture the currently focused cell before adding the row
  const focusedCell = gridApi.value?.getFocusedCell()
  const focusedColumnId = focusedCell?.column?.getColId()
  
  // Add the new row
  addRow()
  emit('addRow')
  
  // Focus and start editing the corresponding cell in the new row
  if (gridApi.value && focusedColumnId) {
    // Use nextTick to ensure the new row is rendered
    nextTick(() => {
      setTimeout(() => {
        if (gridApi.value) {
          // Get the index of the last row (newly added row)
          const newRowIndex = internalRowData.value.length - 1
          
          // Find the column that matches the previously focused column
          const targetColumn = processedColumns.value.find(col => 
            col.field === focusedColumnId && col.editable !== false
          )
          
          if (targetColumn && newRowIndex >= 0) {
            // console.log(`[AgGrid] Focusing new row ${newRowIndex}, column ${focusedColumnId}`)
            
            // Set focus to the corresponding cell in the new row
            gridApi.value.setFocusedCell(newRowIndex, focusedColumnId)
            
            // Start editing the cell
            setTimeout(() => {
              if (gridApi.value) {
                gridApi.value.startEditingCell({
                  rowIndex: newRowIndex,
                  colKey: focusedColumnId
                })
              }
            }, 50)
          } else {
            // Fallback: focus the first editable cell in the new row
            const firstEditableColumn = processedColumns.value.find(col => 
              col.editable !== false && col.field !== 'selected'
            )
            
            if (firstEditableColumn && newRowIndex >= 0) {
              // console.log(`[AgGrid] Fallback focusing new row ${newRowIndex}, column ${firstEditableColumn.field}`)
              gridApi.value.setFocusedCell(newRowIndex, firstEditableColumn.field!)
              
              setTimeout(() => {
                if (gridApi.value && firstEditableColumn.field) {
                  gridApi.value.startEditingCell({
                    rowIndex: newRowIndex,
                    colKey: firstEditableColumn.field
                  })
                }
              }, 50)
            }
          }
        }
      }, 100)
    })
  }
}

// Undo/redo functionality
const handleUndo = () => {
  if (gridApi.value) {
    gridApi.value.undoCellEditing()
    updateUndoRedoState()
  }
}

const handleRedo = () => {
  if (gridApi.value) {
    gridApi.value.redoCellEditing()
    updateUndoRedoState()
  }
}

const updateUndoRedoState = () => {
  if (!gridApi.value) return
  
  const undoSize = gridApi.value.getCurrentUndoSize()
  const redoSize = gridApi.value.getCurrentRedoSize()
  
  undoAvailable.value = undoSize > 0
  redoAvailable.value = redoSize > 0
}

// Zoom and column sizing functionality
const handleZoomIn = () => {
  if (!props.enableZoom || zoomLevel.value >= MAX_ZOOM) return
  
  zoomLevel.value = Math.min(zoomLevel.value + 0.1, MAX_ZOOM)
  const newParams = {
    ...themeParams.value,
    fontSize: Math.round(BASE_FONT_SIZE * zoomLevel.value),
    headerFontSize: Math.round((BASE_FONT_SIZE + 2) * zoomLevel.value),
    rowHeight: Math.round(BASE_ROW_HEIGHT * zoomLevel.value),
    headerHeight: Math.round(BASE_HEADER_HEIGHT * zoomLevel.value),
  }
  themeParams.value = newParams
  
  if (gridApi.value) {
    gridApi.value.resetRowHeights()
    nextTick(() => {
      adjustColumnWidths()
    })
  }
}

const handleZoomOut = () => {
  if (!props.enableZoom || zoomLevel.value <= MIN_ZOOM) return
  
  zoomLevel.value = Math.max(zoomLevel.value - 0.1, MIN_ZOOM)
  const newParams = {
    ...themeParams.value,
    fontSize: Math.round(BASE_FONT_SIZE * zoomLevel.value),
    headerFontSize: Math.round((BASE_FONT_SIZE + 2) * zoomLevel.value),
    rowHeight: Math.round(BASE_ROW_HEIGHT * zoomLevel.value),
    headerHeight: Math.round(BASE_HEADER_HEIGHT * zoomLevel.value),
  }
  themeParams.value = newParams
  
  if (gridApi.value) {
    gridApi.value.resetRowHeights()
    nextTick(() => {
      adjustColumnWidths()
    })
  }
}

// Handle validation tools updates (from PlantGrid.vue)
const handleValidationToolsUpdate = (enabledTools: string[]) => {
  // console.log('[AgGrid] Received validation tools update:', enabledTools)
  enabledValidationTools.value = enabledTools
  
  // Re-emit validation events if we have row data and review on change is disabled
  if (dataSource.value && dataSource.value.length > 0 && reviewOnChange.value) {
    // console.log('[AgGrid] Re-emitting validation events with tools:', enabledValidationTools.value)
    
    const validationData = {
      panelId: (props.panel as any)?.name,
      tool_uid: toolId.value,
      data: {
        rowData: dataSource.value?.map(row => {
          const normalizedRow: Record<string, any> = {}
          
          Object.entries(row).forEach(([key, val]) => {
            if (typeof val === 'object' && val !== null && 'value' in val) {
              normalizedRow[key] = (val as any).value
            } else {
              normalizedRow[key] = val
            }
            
            if (key === 'botanicalname' && typeof val === 'object' && val !== null) {
              normalizedRow[key] = (val as any).label || (val as any).value || ''
              if ((val as any).genus) normalizedRow.genus = (val as any).genus
              if ((val as any).family) normalizedRow.family = (val as any).family
            }
          })
          
          return normalizedRow
        }) || [],
        columnDefs: columnsSource.value
      }
    }
    
    if (enabledValidationTools.value.length > 0) {
      emitValidationToTools(validationData, enabledValidationTools.value)
    }
  } else if (dataSource.value && dataSource.value.length > 0 && !reviewOnChange.value) {
    // console.log('[AgGrid] Skipping validation re-emission - review on change is disabled')
  }
}

// Handle review on change toggle
const handleReviewOnChangeToggle = (newValue: boolean) => {
 
  reviewOnChange.value = newValue
  
  // Update panel data if available
  if (props.panel?.data) {
    props.panel.data.options = {
      ...props.panel.data.options,
      reviewOnChange: reviewOnChange.value
    }
  }
}

// Calculate floating button position based on last row
const updateFloatingButtonPosition = () => {
  if (!gridApi.value || !gridRef.value) {
    floatingButtonPosition.value.visible = false
    return
  }
  
  try {
    // Get the grid element
    const gridElement = gridRef.value.$el || gridRef.value
    if (!gridElement) return
    
    // Find the last row element
    const lastRowElement = gridElement.querySelector('.ag-row-last')
    if (!lastRowElement) {
      floatingButtonPosition.value.visible = false
      return
    }
    
    // Get the row's position and height
    const rowRect = lastRowElement.getBoundingClientRect()
    const gridRect = gridElement.getBoundingClientRect()
    
    // Calculate position relative to grid container
    const relativeTop = rowRect.bottom - gridRect.top + 43 // 8px offset below the row
    
    floatingButtonPosition.value = {
      top: relativeTop,
      visible: true
    }
  } catch (error) {
    console.warn('[AgGrid] Error updating floating button position:', error)
    floatingButtonPosition.value.visible = false
  }
}

// Flag to prevent circular data updates
const isHandlingExternalUpdate = ref(false)

// Handle data updates from external sources or child events
const handleDataUpdate = (newData: any[]) => {
  // console.log('[AgGrid] Handling data update:', newData.length, 'rows')
  
  // Prevent circular updates
  if (isHandlingExternalUpdate.value) {
    // console.log('[AgGrid] Skipping handleDataUpdate - already processing external update')
    return
  }
  
  isHandlingExternalUpdate.value = true
  
  try {
    // Update internal row data with a new array to ensure reactivity
    internalRowData.value = [...newData]
    
    // Update panel data directly without triggering the computed setter
    if (props.panel?.data) {
      props.panel.data.rowData = [...newData]
      // Also update tool_uid if not set
      if (!props.panel.data.tool_uid) {
        props.panel.data.tool_uid = toolId.value
      }
      // Update running state to false to indicate processing is complete
      if (props.panel.data.running !== false) {
        props.panel.data.running = false
      }
    }
    
    // Force AG Grid to update with new data
    if (gridApi.value) {
      nextTick(() => {
        setTimeout(() => {
          if (gridApi.value) {
            // console.log('[AgGrid] Updating grid with new data:', internalRowData.value.length, 'rows')
            gridApi.value.setGridOption('rowData', internalRowData.value)
            
            // Refresh and resize columns after data update
            setTimeout(() => {
              if (gridApi.value) {
                gridApi.value.sizeColumnsToFit()
                // Update button position after grid updates
                setTimeout(updateFloatingButtonPosition, 100)
              }
            }, 100)
          }
        }, 0)
      })
    }
    
    // Don't emit dataUpdate here to prevent circular calls
    // Parent components should listen to grid events or watch panel data directly
  } finally {
    // Clear the flag after a delay to allow the update to complete
    setTimeout(() => {
      isHandlingExternalUpdate.value = false
    }, 200)
  }
}

// Expose methods to parent component
defineExpose({
  addRow,
  updateRow,
  removeRow,
  removeSelectedRows,
  getSelectedRows,
  clearSelection,
  selectAllRows,
  exportToCsv,
  autoSizeAllColumns,
  refreshData,
  setQuickFilter,
  adjustColumnWidths,
  handleToggleSizing,
  handleZoomIn,
  handleZoomOut,
  handleUndo,
  handleRedo,
  updateUndoRedoState,
  handleDataUpdate,
  gridApi: () => gridApi.value,
  // Cell response system methods
  cellResponseSystem: {
    registerRule: cellResponseSystem.registerRule,
    unregisterRule: cellResponseSystem.unregisterRule,
    setAsyncOperationsEnabled: cellResponseSystem.setAsyncOperationsEnabled,
    clearQueue: cellResponseSystem.clearQueue,
    queueStatus: cellResponseSystem.queueStatus,
    // Botanical-specific methods if using botanical system
    ...(props.enableTransformers ? {
      extractGenus: (cellResponseSystem as any).extractGenus,
      generatePlantCode: (cellResponseSystem as any).generatePlantCode,
      parseCellValue: (cellResponseSystem as any).parseCellValue,
      createBotanicalRule: (cellResponseSystem as any).createBotanicalRule
    } : {})
  },
  // Panel communication methods
  panelSystem: {
    initializePanelConnections,
    autoConnectToDefaults,
    handleIncomingPanelData,
    emitGridData,
    panelConnections: () => panelConnectionsRef.value,
    panelData: {
      register: (panelId: string, data?: any) => registerPanel(panelId, data),
      update: (panelId: string, data: any) => updatePanelData(panelId, data),
      get: (panelId: string) => getPanelData(panelId)
    }
  },
  // Advanced panel features from PlantGrid.vue
  advancedFeatures: {
    handleValidationToolsUpdate,
    enabledValidationTools: () => enabledValidationTools.value,
    updateQueue: () => updateQueue.value,
    findAndUpdateGridRow,
    updateGridNode,
    emitValidationToTools
  },
  updateBiodiversityReadiness,
  biodiversityReadiness: readonly(biodiversityReadiness),
  // Color styling methods
  colorSystem: {
    selectedColorRange: selectedColorRange,
    calculateDataRanges,
    clearColorCache: () => {
      colorCacheVersion.value++
      colorValueCache.value.clear()
    },
    refreshColors: () => {
      if (gridApi.value) {
        calculateDataRanges()
        colorCacheVersion.value++
        colorValueCache.value.clear()
        gridApi.value.refreshCells({ force: true })
      }
    },
    setColorSelections: (selections: SliderSelection[]) => {
      selectedColorRange.value = [...selections]
    },
    getDataRanges: () => dataRanges.value,
    // Color palettes available for use
    colorPalettes: readonly(colorPalettes)
  }
})

// Watch for data changes and emit panel events
watch(() => dataSource.value, (newData) => {
  // Skip panel events if we're handling an external update to prevent circular loops
  if (isHandlingExternalUpdate.value) {
    // console.log('[AgGrid] Skipping panel events - handling external update')
    return
  }
  
  if (effectivePanelEventsEnabled.value && newData) {
    // console.log('[AgGrid] Data changed, scheduling panel event emission')
    emitGridData()
  }
  
  // Sync with panel data management if enabled
  if (props.enablePanelPersistence && props.panel && newData) {
    updatePanelData((props.panel as any).name, { rowData: newData })
  }
  
  // Update floating button position when data changes
  nextTick(() => {
    setTimeout(updateFloatingButtonPosition, 100)
  })
}, { deep: true })

// Enhanced panel data synchronization - bidirectional sync
watch(() => props.panel?.data, (newPanelData) => {
  if (props.enablePanelPersistence && newPanelData?.rowData && newPanelData.rowData !== dataSource.value) {
    // console.log('[AgGrid] Panel data changed externally, syncing with grid')
    // Only update if the data is actually different to prevent infinite loops
    if (JSON.stringify(newPanelData.rowData) !== JSON.stringify(dataSource.value)) {
      internalRowData.value = [...(newPanelData.rowData || [])]
      if (gridApi.value) {
        setTimeout(() => {
          if (gridApi.value) {
            gridApi.value.setGridOption('rowData', internalRowData.value)
          }
        }, 0)
      }
    }
  }
}, { deep: true })

// Watch for panel.data.output changes to process file analysis results
watch(() => props.panel?.data?.output, (newOutput, oldOutput) => {
  // Skip if no data or if processing or if the same reference (to avoid loops)
  if (!newOutput || isProcessingOutput.value || newOutput === oldOutput) {
    return
  }

  // console.log('[AgGrid] Panel data output changed:', newOutput)
  
  // Process the output similar to PlantGrid.vue
  if (newOutput && typeof newOutput === 'object') {
    isProcessingOutput.value = true
    
    try {
      // Handle JSON format from file analysis
      if (newOutput.json && Array.isArray(newOutput.json)) {
        // console.log('[AgGrid] Processing file analysis JSON data:', newOutput.json.length, 'rows')
        
        // Update the grid data directly
        handleDataUpdate(newOutput.json)
        
        // Update panel data to keep it in sync
        if (props.panel?.data) {
          props.panel.data.rowData = [...newOutput.json]
          // Skip column def updates to avoid type issues
        }
      } else {
        // Try to parse as analysis result using the parser
        const parser = newOutput?.model?.toLowerCase().includes('gemini') ? 'gemini' : 'llamaparse'
        const parsedData = parse(newOutput, parser)
        
        if (parsedData && parsedData.rowData) {
          // console.log('[AgGrid] Processing parsed analysis data:', parsedData.rowData.length, 'rows')
          
          // Update the grid data
          handleDataUpdate(parsedData.rowData)
          
          // Update panel data to keep it in sync  
          if (props.panel?.data) {
            props.panel.data.rowData = [...parsedData.rowData]
            if (parsedData.columnDefs) {
              props.panel.data.columnDefs = parsedData.columnDefs
            }
          }
        }
      }
    } catch (error) {
      console.error('[AgGrid] Error processing panel output:', error)
    } finally {
      setTimeout(() => {
        isProcessingOutput.value = false
      }, 100)
    }
  }
}, { deep: true })

// Watch for data changes to emit validation events (from PlantGrid.vue)
// OPTIMIZATION: Faster debounce since external API transformers now apply results immediately
watchDebounced(() => dataSource.value, (newRowData) => {
  if (!newRowData || !Array.isArray(newRowData) || newRowData.length === 0) return
  
  // console.log('[AgGrid Validation Watch] Data changed, scheduling validation check (external API transformers already applied)')
  
  // Prepare common data for all validation events
  const validationData = {
    panelId: (props.panel as any)?.name || 'unknown',
    tool_uid: toolId.value,
    data: {
      rowData: newRowData?.map(row => {
        const normalizedRow: Record<string, any> = {}
        
        Object.entries(row).forEach(([key, val]) => {
          if (typeof val === 'object' && val !== null && 'value' in val) {
            normalizedRow[key] = (val as any).value
          } else {
            normalizedRow[key] = val
          }
          
          if (key === 'botanicalname' && typeof val === 'object' && val !== null) {
            normalizedRow[key] = (val as any).label || (val as any).value || ''
            if ((val as any).genus) normalizedRow.genus = (val as any).genus
            if ((val as any).family) normalizedRow.family = (val as any).family
          }
        })
        
        return normalizedRow
      }) || [],
      columnDefs: columnsSource.value
    }
  }
  
  // Only emit validation events if there's actual data and validation tools are enabled
  if (newRowData.length > 1 || (newRowData.length === 1 && hasAnyValues(newRowData[0]))) {
    if (effectiveValidationTools.value?.length && reviewOnChange.value) {
      // console.log('[AgGrid Validation Watch] Emitting validation events for', newRowData.length, 'rows (external API transformers already applied)')
      emitValidationToTools(validationData, effectiveValidationTools.value)
    } else if (!reviewOnChange.value) {
      // console.log('[AgGrid Validation Watch] Skipping validation - review on change is disabled')
    }
  }
}, { deep: true, debounce: 1200 }) // Reduced from 3000ms to 1200ms since external API transformers now apply results immediately

// Helper to check if any row has actual values
const hasAnyValues = (row: any) => {
  return Object.entries(row).some(([key, val]) => {
    if (key === 'selected' || key === 'checkboxSelection') return false
    
    if (typeof val === 'object' && val !== null && 'value' in val) {
      return (val as any).value !== null && (val as any).value !== undefined && (val as any).value !== ''
    }
    
    return val !== null && val !== undefined && val !== ''
  })
}

// Ref for the color trigger/circle container
import { ref as vueRef } from 'vue'
const colorTriggerRef = vueRef(null)
const isColorHovered = useElementHover(colorTriggerRef, { delayLeave: 150 })

// Toggle state for each badge (by index)
const toggleBadge = (index: number) => {
  if (selectedColorRange.value[index]) {
    selectedColorRange.value[index].isActive = !selectedColorRange.value[index].isActive
    
    // Force a redraw to apply the style change
    if (gridApi.value) {
      gridApi.value.redrawRows()
    }
  }
}

const BADGE_WIDTH_PX = 48 // w-12 = 3rem = 48px
const BADGE_MARGIN_PX = 8 // mx-1 = 0.5rem = 8px

// Check if any color schemes are currently active
const hasActiveSchemes = computed(() => {
  return selectedColorRange.value.some(selection => selection.isActive)
})

// Auto-select family coloring when popover opens if no schemes are active
const handleColorPopoverOpen = () => {
  if (!hasActiveSchemes.value && !uiStore.hasActiveColorSchemes) {
    // Create a default family color selection
    const defaultFamilySelection = {
      id: 'family-default',
      method: 'family',
      palette: 'qualitative',
      range: [0, 1] as [number, number],
      label: 'Plant Families',
      isActive: true
    }
    
    selectedColorRange.value = [defaultFamilySelection]
    
    // Update UI store to track that we now have active schemes
    uiStore.setHasActiveColorSchemes(true)
  }
}

</script>

<template>
  <div class="table-container">
    <!-- Color By Button - Left Side -->
    <div v-if="showFloatingControls" class="group/color absolute top-3 left-4 z-10 flex items-center gap-2 pointer-events-none">
      <Popover :modal="false" @update:open="(open) => open && handleColorPopoverOpen()">
        <PopoverTrigger as-child>
          <div class="relative" ref="colorTriggerRef">
            <!-- Active selection gradient circles -->
            <div 
              v-for="(gradient, index) in activeSelectionGradients" 
              :key="index"
              class="absolute select-none top-1 w-12 h-7 rounded-full transition-all duration-300 pointer-events-auto flex items-center justify-center px-3 cursor-pointer"
              :class="[
                isColorHovered ? 'opacity-100' : 'opacity-0',
                isColorHovered && !selectedColorRange[index]?.isActive ? 'opacity-40 grayscale' : ''
              ]"
              :style="{ 
                background: gradient,
                left: '48px',
                transform: isColorHovered ? `translateX(${index * (BADGE_WIDTH_PX + BADGE_MARGIN_PX)}px)` : 'translateX(-12px)'
              }"
              @click.stop="toggleBadge(index)"
            >
              <span class="text-xs text-primary-foreground font-medium truncate">{{ selectedColorRange[index]?.label || selectedColorRange[index]?.field || '' }}</span>
            </div>
            <!-- Main trigger button -->
            <Button
              variant="ghost"
              class="group/color-trigger rounded-full pointer-events-auto rainbow-gradient-border-animated p-1 text-xs aspect-square h-9 w-9 relative z-10"
            >
              <Icon name="teenyicons:paintbucket-solid" class="w-4 h-4 text-primary/90 group-hover/color-trigger:text-white" />
            </Button>
          </div>
        </PopoverTrigger>
        <PopoverContent 
          align="start" 
          side="left"
          
          :sideOffset="-87" 
          class="w-80 rounded-2xl border-none shadow-xxl"
          :collision-padding="8"
        >
          <p v-if="!selectedColorRange.length" class="text-md pt-4 pl-4 font-semibold tracking-tight">Color by performance</p>
          <p v-if="!selectedColorRange.length" class="text-sm pt-4 pl-4 font-[400] mt-0 ">Easily see what needs changing by coloring different aspects of your plant schedule.</p>
          <DivergentSliderPicker v-model="selectedColorRange" />
        </PopoverContent>
      </Popover>
    </div>

    <!-- Floating Controls -->
    <div v-if="showFloatingControls" class="absolute top-2 right-2 z-10 flex items-center gap-2 pointer-events-none">
      <div class="flex items-center gap-2 pointer-events-auto">
        <!-- Zoom and sizing controls -->
        <div v-if="enableZoom" class="flex items-center">
          <div class="relative group-zoom">
            <Button
              variant="ghost"
              size="icon"
              class="rounded-full translate-x-16 opacity-0 group-hover-zoom:opacity-100 group-hover-zoom:translate-x-0 transition-all duration-300 absolute right-20"
              :class="[useFitContents ? 'text-primary' : 'text-muted-foreground']"
              @click="handleToggleSizing"
            >
              <Icon name="fluent:auto-fit-width-20-filled" class="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              class="rounded-full ml-2"
              @click="handleZoomIn"
              :disabled="zoomLevel >= MAX_ZOOM"
            >
              <Icon name="lucide:zoom-in" class="w-4 h-4" />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              class="rounded-full translate-x-8 opacity-0 group-hover-zoom:opacity-100 group-hover-zoom:translate-x-0 transition-all duration-300 absolute right-10"
              @click="handleZoomOut"
              :disabled="zoomLevel <= MIN_ZOOM"
            >
              <Icon name="lucide:zoom-out" class="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        <!-- Undo button -->
        <Button
          variant="ghost"
          size="sm"
          class="rounded-full"
          @click="handleUndo"
          :disabled="!undoAvailable"
          title="Undo (Ctrl+Z)"
        >
          <Icon name="solar:undo-left-round-linear" class="w-4 h-4" />
        </Button>

        <!-- Add Row button -->
        <Button
          v-if="showAddButton"
          variant="ghost"
          size="sm"
          class="rounded-full"
          @click="handleAddRow"
        >
          <Icon name="lucide:plus" class="w-4 h-4 mr-2" />
          <span>Add Row</span>
        </Button>

        <!-- Delete button (only show when rows are selected) -->
        <Button
          v-if="(rowSelection === 'multiple' || checkboxSelection) && selectedRowCount > 0"
          variant="ghost"
          size="sm"
          class="rounded-full"
          @click="removeSelectedRows"
        >
          <Icon name="lucide:trash-2" class="w-4 h-4 mr-2" />
          <span>Delete {{ selectedRowCount > 1 ? `${selectedRowCount} Rows` : 'Row' }}</span>
        </Button>

        <!-- Dropdown menu -->
        <DropdownMenu>
          <DropdownMenuTrigger as="div">
            <Button 
              size="sm" 
              variant="ghost"
              class="self-center rounded-full w-8 h-8 p-0"
            >
              <Icon name="mdi:dots-vertical" class="w-5 h-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent class="min-w-[180px] rounded-xl p-2">
            <DropdownMenuItem @click="handleRedo" :disabled="!redoAvailable">
              <Icon name="solar:undo-right-round-linear" class="w-4 h-4 mr-2" />
              Redo
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem @click="exportToCsv()">
              <Icon name="lucide:download" class="w-4 h-4 mr-2" />
              Export CSV
            </DropdownMenuItem>
            <DropdownMenuItem @click="autoSizeAllColumns">
              <Icon name="lucide:columns" class="w-4 h-4 mr-2" />
              Auto Size Columns
            </DropdownMenuItem>
            <DropdownMenuItem @click="refreshData">
              <Icon name="lucide:refresh-cw" class="w-4 h-4 mr-2" />
              Refresh
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <div 
              class="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
              @click.stop.prevent="handleReviewOnChangeToggle(!reviewOnChange)"
            >
              <div class="flex items-center justify-between w-full">
                <div class="flex items-center">
                  <span>Changes trigger review</span>
                </div>
                <Switch 
                  :key="`review-switch-${reviewOnChange}`"
                  :model-value="reviewOnChange" 
                  :checked="reviewOnChange"
                  class="ml-2 cursor-pointer"
                />
              </div>
            </div>
            <DropdownMenuSeparator v-if="rowSelection" />
            <DropdownMenuItem @click="clearSelection" v-if="rowSelection">
              <Icon name="lucide:square" class="w-4 h-4 mr-2" />
              Clear Selection
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>

    <!-- Loading overlay -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner">Loading...</div>
    </div>
    
    <!-- Quick filter -->
    <div class="table-toolbar" v-if="enableFiltering && !showFloatingControls">
      <div class="quick-filter">
        <input
          type="text"
          placeholder="Search..."
          @input="setQuickFilter(($event.target as HTMLInputElement).value)"
          class="filter-input"
        />
      </div>
      <div class="table-actions">
        <button 
          v-if="rowSelection" 
          @click="clearSelection"
          class="btn-secondary"
        >
          Clear Selection
        </button>
        <button 
          v-if="rowSelection === 'multiple'" 
          @click="removeSelectedRows"
          class="btn-danger"
        >
          Remove Selected
        </button>
        <button 
          @click="exportToCsv()"
          class="btn-primary"
        >
          Export CSV
        </button>
        <button 
          @click="autoSizeAllColumns"
          class="btn-secondary"
        >
          Auto Size
        </button>
      </div>
    </div>

          <!-- AG Grid -->
      <div 
        class="grid-wrapper p-4 pb-8 relative group/table"
        :style="{ 
          height: typeof height === 'number' ? `${height}px` : height,
        paddingTop: showFloatingControls ? '60px' : '0'
      }"
    >
      <AgGridVue
        ref="gridRef"
        :theme="customTheme"
        :style="gridStyle"
        :rowData="internalRowData"
        :columnDefs="processedColumns"
        :defaultColDef="processedDefaultColDef"
        :gridOptions="gridOptions"
        @grid-ready="onGridReady"
        @cell-value-changed="onCellValueChanged"
        @cell-editing-started="onCellEditingStarted"
        @cell-editing-stopped="onCellEditingStopped"
        @row-selected="onRowSelected"
        @selection-changed="onSelectionChanged"
        class="ag-grid font-intervariable"
      />
      
      <!-- Floating Add Row Button -->
      <div 
        v-if="(showAddButton || !showFloatingControls) && floatingButtonPosition.visible" 
        class="absolute left-14 z-20 pointer-events-none transition-all duration-200 border-[2px] border-background rounded-full opacity-0 group-hover/table:opacity-100 group-hover/table:translate-y-0 translate-y-2 duration-100"
        :style="{ top: `${floatingButtonPosition.top}px` }"
      >
        <Button
          @click="handleAddRow"
          size="sm"
          class="rounded-full h-5 w-5 p-0 border-[1px] border-primary/60 bg-background group-hover/table:scale-105
          text-primary-foreground pointer-events-auto transition-translate "
          title="Add new row"
        >
          <Icon name="ic:round-add" class="w-5 h-5 text-primary/60 hover:text-background" />
        </Button>
      </div>
    </div>
  </div>

  <!-- Help Video Popover for Quantity Column -->
  <HelpVideoPopover 
    v-if="quantityColumnHeaderRef"
    :visible="quantityColumnHelp.state.value?.visible || false"
    :dismissed="quantityColumnHelp.state.value?.dismissed || false"
    :sticky="true"
    :target-element="quantityColumnHeaderRef"
    :arrow="true"
    :disableUpdateOnLayoutShift="false"
    :hideWhenDetached="false"
    :prioritizePosition="false"
    :arrow-position="'middle-top'"

    follow-axis="none"
    :dismiss-on-distance="false"
    :dismiss-on-target-click="false"
    :dismiss-on-click-outside="false"
    video-id="j5HSdcCpEjg"
    :current-step-title="quantityColumnHelp.state.value?.title"
    :current-step-description="quantityColumnHelp.state.value?.description"
    @hide="() => quantityColumnHelp.hide()"
    @dismiss="() => quantityColumnHelp.dismiss()"
  />
</template>

<style scoped>
.table-container {
  position: relative;
  width: 100%;
  background: hsl(var(--background));
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: hsl(var(--background) / 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.loading-spinner {
  padding: 20px;
  font-size: 14px;
  color: hsl(var(--muted-foreground));
}

.table-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  gap: 16px;
  flex-wrap: wrap;
}

.quick-filter {
  flex: 1;
  min-width: 200px;
}

.filter-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid hsl(var(--border));
  border-radius: 4px;
  font-size: 14px;
  background: hsl(var(--background));
  color: hsl(var(--foreground));
}

.filter-input:focus {
  outline: none;
  border-color: hsl(var(--ring));
  box-shadow: 0 0 0 2px hsl(var(--ring) / 0.25);
}

.table-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-primary, .btn-secondary, .btn-danger, .btn-ghost {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-icon {
  padding: 8px;
  width: 32px;
  height: 32px;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 13px;
}

.btn-primary {
  background: hsl(var(--primary));
  color: hsl(var(--primary-foreground));
}

.btn-primary:hover {
  background: hsl(var(--primary) / 0.9);
}

.btn-secondary {
  background: hsl(var(--secondary));
  color: hsl(var(--secondary-foreground));
}

.btn-secondary:hover {
  background: hsl(var(--secondary) / 0.8);
}

.btn-danger {
  background: hsl(var(--destructive));
  color: hsl(var(--destructive-foreground));
}

.btn-danger:hover {
  background: hsl(var(--destructive) / 0.9);
}

.btn-ghost {
  background: transparent;
  color: hsl(var(--foreground));
}

.btn-ghost:hover {
  background: hsl(var(--accent));
  color: hsl(var(--accent-foreground));
}

.btn-ghost:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.grid-wrapper {
  width: 100%;
}

.ag-grid {
  height: 100%;
  width: 100%;
}

/* AG Grid theme customizations using Tailwind variables */
:deep(.ag-theme-quartz) {
  --ag-font-size: 13px;
  --ag-font-family: InterVariable, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  --ag-border-radius: 0;
  --ag-header-height: 40px;
  --ag-row-height: 36px;
  --ag-header-background-color: hsl(var(--muted));
  --ag-header-foreground-color: hsl(var(--muted-foreground));
  --ag-border-color: hsl(var(--border));
  --ag-row-border-color: hsl(var(--border));
  --ag-odd-row-background-color: hsl(var(--background));
  --ag-even-row-background-color: hsl(var(--muted) / 0.3);
  --ag-foreground-color: hsl(var(--foreground));
  --ag-secondary-foreground-color: hsl(var(--muted-foreground));
  --ag-background-color: hsl(var(--background));
  --ag-data-color: hsl(var(--foreground));
  --ag-selected-row-background-color: hsl(var(--accent) / 0.1);
  --ag-cell-horizontal-border: hsl(var(--border));
  --ag-input-background-color: hsl(var(--input));
  --ag-input-border-color: hsl(var(--border));
  --ag-input-focus-border-color: hsl(var(--ring));
}

:deep(.ag-header) {
  border-bottom: 2px solid hsl(var(--border));
}

:deep(.ag-header-cell) {
  font-weight: 600;
  font-size: 12px;
  color: hsl(var(--muted-foreground));
  padding: 0 12px;
}

:deep(.ag-header-cell-text) {
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.ag-cell) {
  padding: 0 12px;
  display: flex;
  align-items: center;
  border-right: 1px solid hsl(var(--border));
}

:deep(.ag-cell-editing) {
  padding: 0 !important;
  border: 2px solid hsl(var(--primary)) !important;
  border-radius: 4px !important;
  background: hsl(var(--background)) !important;
}

:deep(.ag-cell-editing .ag-cell-wrapper) {
  padding: 0 !important;
  height: 100% !important;
}

:deep(.ag-cell-editing input) {
  border: none !important;
  outline: none !important;
  background: transparent !important;
  padding: 8px 12px !important;
  width: 100% !important;
  height: 100% !important;
  font-size: 13px !important;
  color: hsl(var(--foreground)) !important;
  border-radius: 2px !important;
}

:deep(.ag-cell-editing .ag-text-field-input) {
  border: none !important;
  outline: none !important;
  background: transparent !important;
  padding: 8px 12px !important;
  width: 100% !important;
  height: 100% !important;
  font-size: 13px !important;
  color: hsl(var(--foreground)) !important;
  box-shadow: none !important;
}

:deep(.ag-cell-editing .ag-number-field-input) {
  border: none !important;
  outline: none !important;
  background: transparent !important;
  padding: 8px 12px !important;
  width: 100% !important;
  height: 100% !important;
  font-size: 13px !important;
  color: hsl(var(--foreground)) !important;
  box-shadow: none !important;
}

:deep(.ag-cell-editing .ag-input-field-input) {
  border: none !important;
  outline: none !important;
  background: transparent !important;
  padding: 8px 12px !important;
  width: 100% !important;
  height: 100% !important;
  font-size: 13px !important;
  color: hsl(var(--foreground)) !important;
  box-shadow: none !important;
}

:deep(.ag-cell-editing .ag-input-wrapper) {
  border: none !important;
  background: transparent !important;
  padding: 0 !important;
  height: 100% !important;
}

:deep(.ag-cell-editing .ag-wrapper) {
  border: none !important;
  background: transparent !important;
  padding: 0 !important;
  height: 100% !important;
}

/* Colored background using pseudo-element without position relative */
:deep(.ag-cell[style*="--cell-bg-color"]::before) {
  content: '';
  position: absolute;
  top: 4px;
  left: 4px;
  right: 4px;
  bottom: 4px;
  background-color: var(--cell-bg-color);
  border-radius: 4px;
  pointer-events: none;
  z-index: -1;
}

/* Never apply colors to checkbox column */
:deep(.ag-cell[col-id="selected"]::before) {
  display: none !important;
}

:deep(.ag-cell[col-id="selected"]) {
  background: transparent !important;
  background-color: transparent !important;
}

/* Override row-level background colors for checkbox column */
:deep(.ag-row .ag-cell[col-id="selected"]) {
  background: hsl(var(--background)) !important;
  background-color: hsl(var(--background)) !important;
}

:deep(.ag-row) {
  border-bottom: 1px solid hsl(var(--border));
}

:deep(.ag-cell-focus) {
  border: 1px solid hsl(var(--ring)) !important;
  outline: none;
}

:deep(.ag-checkbox) {
  font-size: 14px;
}

:deep(.ag-checkbox-input-wrapper) {
  position: relative !important;
  width: 18px !important;
  height: 18px !important;
  border-radius: 4px !important;
  border: 2px solid hsl(var(--primary)) !important;
  background: hsl(var(--background)) !important;
  transition: all 0.2s ease !important;
}

:deep(.ag-checkbox-input-wrapper.ag-checked) {
  background: hsl(var(--primary)) !important;
  border-color: hsl(var(--primary)) !important;
}

:deep(.ag-checkbox-input-wrapper:hover) {
  border-color: hsl(var(--primary)) !important;
  box-shadow: 0 0 0 2px hsl(var(--ring) / 0.2) !important;
}

:deep(.ag-checkbox-input) {
  opacity: 0 !important;
  position: absolute !important;
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  cursor: pointer !important;
}

:deep(.ag-checkbox-input-wrapper.ag-checked::after) {
  content: '✓' !important;
  position: absolute !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  color: hsl(var(--primary-foreground)) !important;
  font-size: 12px !important;
  font-weight: bold !important;
  line-height: 1 !important;
}

:deep(.ag-checkbox-input:focus + .ag-checkbox-input-wrapper),
:deep(.ag-checkbox-input-wrapper:focus-within) {
  outline: none !important;
  ring: 2px !important;
  ring-color: hsl(var(--ring)) !important;
  ring-offset: 2px !important;
  ring-offset-color: hsl(var(--background)) !important;
}

:deep(.ag-checkbox-input:disabled + .ag-checkbox-input-wrapper) {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
}

:deep(.ag-cell[col-id="selected"]) {
  justify-content: center !important;
  padding: 8px !important;
}

:deep(.ag-header-cell[col-id="selected"]) {
  justify-content: center !important;
  padding: 0 !important;
}

:deep(.ag-header-cell[col-id="checkboxSelection"] .ag-header-cell-menu-button) {
  display: none !important;
}

:deep(.ag-cell[col-id="selected"]) {
  justify-content: center !important;
  padding: 8px !important;
}

:deep(.ag-header-cell[col-id="selected"]) {
  justify-content: center !important;
  padding: 0 !important;
}

:deep(.ag-header-cell[col-id="selected"] .ag-header-cell-filter-button) {
  display: none !important;
}

:deep(.ag-paging-panel) {
  border-top: 1px solid hsl(var(--border));
  background: hsl(var(--muted));
  padding: 12px 20px;
  font-size: 13px;
  color: hsl(var(--muted-foreground));
}

:deep(.ag-paging-button) {
  color: hsl(var(--muted-foreground));
  font-size: 13px;
}

:deep(.ag-paging-button:not(.ag-disabled)) {
  color: hsl(var(--foreground));
}

:deep(.ag-paging-button:not(.ag-disabled):hover) {
  background: hsl(var(--accent));
  color: hsl(var(--accent-foreground));
}

/* Search input styling for autocomplete columns */
:deep(.ag-cell-wrapper) {
  width: 100%;
}

:deep(.ag-cell input[type="text"]) {
  width: 100%;
  border: none;
  background: transparent;
  padding: 4px 0;
  font-size: 13px;
  outline: none;
  color: hsl(var(--foreground));
}

:deep(.ag-cell input[type="text"]:focus) {
  background: hsl(var(--background));
  border: 1px solid hsl(var(--ring));
  border-radius: 3px;
  padding: 4px 8px;
}

/* Floating controls styling */
.group-zoom:hover .group-hover-zoom\:opacity-100 {
  opacity: 1;
}

.group-zoom:hover .group-hover-zoom\:translate-x-0 {
  transform: translateX(0);
}

.text-primary {
  color: hsl(var(--primary));
}

.text-muted-foreground {
  color: hsl(var(--muted-foreground));
}

/* Enhanced tooltip styling */
:deep(.ag-tooltip) {
  background: hsl(var(--popover)) !important;
  background-color: hsl(var(--popover)) !important;
  border: 1px solid hsl(var(--border)) !important;
  border-radius: 8px !important;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
  font-family: InterVariable, sans-serif !important;
  z-index: 1000 !important;
  
  /* Fixed width constraints to prevent layout shifts */
  min-width: 200px !important;
  max-width: 320px !important;
  width: auto !important;
  
  /* Remove transitions entirely to test if they're causing the issue */
  /* opacity: 1 !important;
  transition: opacity 80ms ease-out !important; */
}



/* AG Grid hiding state for smooth exit - opacity only */
:deep(.ag-tooltip.ag-tooltip-hiding) {
  opacity: 0 !important;
  transition: opacity 200ms ease-in !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  :deep(.ag-cell) {
    padding: 0 8px;
  }
  
  :deep(.ag-header-cell) {
    padding: 0 8px;
  }
  
  .table-toolbar {
    flex-direction: column;
    align-items: stretch;
  }
  
  .table-actions {
    justify-content: center;
  }
  
  .filter-input {
    margin-bottom: 8px;
  }
  
  /* Hide some floating controls on mobile to save space */
  .absolute.top-2.right-2 {
    flex-wrap: wrap;
    gap: 1px;
  }
  
  /* Responsive tooltip adjustments */
  :deep(.ag-tooltip-custom) {
    max-width: 280px;
    font-size: 12px;
    padding: 10px 12px;
  }
}

/* Remove selection border from checkbox column cells specifically */
:deep(.ag-cell[col-id="selected"].ag-cell-range-selected),
:deep(.ag-cell[col-id="selected"].ag-cell-focus) {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

/* Completely disable tooltips for checkbox column */
:deep(.ag-cell[col-id="selected"]) {
  pointer-events: auto !important;
}

:deep(.ag-cell[col-id="selected"]:hover) {
  cursor: pointer !important;
}

:deep(.ag-header-cell[col-id="selected"]) {
  pointer-events: auto !important;
}

/* Floating Add Row Button Styling */
.grid-wrapper {
  position: relative;
}

/* Prevent any tooltip from showing on checkbox column */
:deep(.ag-cell[col-id="selected"] .ag-tooltip),
:deep(.ag-header-cell[col-id="selected"] .ag-tooltip) {
  display: none !important;
  visibility: hidden !important;
}

/* Remove range selection styling from checkbox column */
:deep(.ag-cell[col-id="selected"].ag-cell-range-selected-1),
:deep(.ag-cell[col-id="selected"].ag-cell-range-selected-2),
:deep(.ag-cell[col-id="selected"].ag-cell-range-selected-3),
:deep(.ag-cell[col-id="selected"].ag-cell-range-selected-4) {
  border: none !important;
  background-color: transparent !important;
}

/* Ensure checkbox column cells don't show selection borders */
:deep(.ag-cell[col-id="selected"]) {
  border-right: none !important;
  border-left: none !important;
  
}

:deep(.ag-cell-focus) {
  border: 2px solid hsl(var(--ring)) !important;
  border-radius: 4px !important;
  outline: none;
}

/* Cell renderer components handle their own styling */

:deep(.ag-tooltip-custom) {
  padding: 12px 16px !important;
  background: hsl(var(--background)) !important;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
  border-radius: 8px !important;
  font-family: InterVariable, sans-serif !important;
  z-index: 1000 !important;
  
  /* Fixed width constraints for custom tooltip content */
  min-width: 200px !important;
  max-width: 320px !important;
  width: auto !important;
  box-sizing: border-box !important;
  
  /* Remove transitions to test */
  /* opacity: 1 !important;
  transition: opacity 80ms ease-out !important;
  animation: tooltipFadeIn 80ms ease-out !important; */
}

:deep(.ag-tooltip-custom.ag-tooltip-hiding) {
  opacity: 0 !important;
  transition: opacity 200ms ease-in !important;
  scale: 0.9 !important;
}


@tailwind base;
@tailwind components;
@tailwind utilities;

@layer components {
  .rainbow-gradient-border {
    @apply relative border-4 border-transparent;
    background: 
      linear-gradient(white, white) padding-box,
      conic-gradient(from 0deg, 
        #ffbe0b 0deg,
        #fb5607 72deg,
        #ff006e 144deg,
        #8338ec 216deg,
        #3a86ff 288deg,
        #ffbe0b 360deg
      ) border-box;
  }

  .rainbow-gradient-border:hover {
    background: 
      conic-gradient(from 0deg, 
        #ffbe0b 0deg,
        #fb5607 72deg,
        #ff006e 144deg,
        #8338ec 216deg,
        #3a86ff 288deg,
        #ffbe0b 360deg
      ) !important;
    @apply border-none;
  }

  .rainbow-gradient-border-animated {
    @apply relative overflow-hidden;
  }

  .rainbow-gradient-border-animated::before {
    content: '';
    @apply absolute inset-0 rounded-full;
    background: conic-gradient(
      from 0deg,
      #ffbe0b 0deg,
      #fb5607 72deg,
      #ff006e 144deg,
      #8338ec 216deg,
      #3a86ff 288deg,
      #ffbe0b 360deg
    );
    animation: rainbow-rotate 3s linear infinite;
    z-index: -1;
  }

  .rainbow-gradient-border-animated::after {
    content: '';
    @apply absolute inset-1 rounded-full bg-background;
    z-index: -1;
    transition: inset 0.3s ease;
  }

  .rainbow-gradient-border-animated:hover::before {
    animation: rainbow-rotate-fast 3s linear infinite;
  }

  .rainbow-gradient-border-animated:hover::after {
    @apply inset-0;
    opacity: 0;
  }
  
  .rainbow-gradient-border-animated > * {
    @apply relative z-10;
  }
}

@keyframes rainbow-rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes rainbow-rotate-fast {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

</style>